import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import DocumentTitle from 'react-document-title';
import { parse, stringify } from 'qs';
import { change, submit, SubmissionError } from 'redux-form';
import { NoPrint } from 'react-easy-print';
import _ from 'lodash';
import store from '../../store';
import queryClient from '../../react-query-client';
import {
  always,
  and,
  any,
  curryN,
  either,
  equals,
  evolve,
  ifElse,
  intersection,
  isEmpty,
  isNil,
  not,
  omit,
  path,
  pathEq,
  pathOr,
  pick,
  reject,
} from 'ramda';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import withTransferTargetPrograms from './withTransferTargetPrograms';
import {
  isEditHouseholdMemberDatesFlagEnabled,
  isHouseholdMiMoDateChangesFlagEnabled,
} from './hooks';
import {
  formatCalendarDateUtc,
  formatDateLocale,
} from '../../utils/date-helpers';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Tooltip } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { LazyLoad } from '@fortress-technology-solutions/fortress-component-library/HOC';
import { MiniProfileBridge } from './MiniProfileBridge';
import AddHouseholdMemberModal from './AddHouseholdMemberModal';
import TextingTab from '../TextingTab';
import {
  ELECTRONIC_SIGNING_METHOD,
  getSelectedLease,
  mapCommercialLeaseBasicsToLeaseData,
  mapFormValuesLeaseData,
} from '../../utils/lease-helpers';
import { isValidResidentId } from '../../utils/resident-helpers';
import CamTab from '../CamTab';
import Tabs from './Tabs';
import { PRORATE_METHODS } from '../../components/CompleteRenewalCheck/RenewalProrates/constants';
import { MOVE_OUT_PRORATE_MEHTODS, TAB_KEYS } from './constants';

/* Foreign Imports */
// Actions
import * as applicationActions from '../ApplicationProfile/actions';
import {
  assignApplication,
  updateApplication,
} from '../ApplicationProfile/actions';
import * as prospectActions from '../PeopleProfile/actions';
import { getCustomerLedgerHeader, refreshLedger } from '../Ledger/actions';
import {
  getAllActivityTypes,
  getAllAffordableRelationships,
  getAllApplicantCancellationReasons,
  getAllApplicantTypes,
  getAllAssignees,
  getAllContactTypes,
  getAllCountries,
  getAllMoveOutReasons,
  getAllHousingSituations,
  getAllNameSuffixes,
  getAllPetBreeds,
  getAllPetTypes,
  getAllProspectStatus,
  getAllReferralTypes,
  getAllRelationships,
  getAllStates,
  getAllProrateMethods,
  getAllMoveOutProrateMethods,
  getMoveOutProratedTransactions as getStartOfMonthMoveOutProratedTransactons,
  selectProperty,
  updateSelectedProperty,
  getAllSpecialNeedsDesignations,
  promptToaster,
} from '../App/actions';
import { getResidentHousehold } from '../EditResidentHousehold/actions';
import { getSignatureStatuses } from '../LeaseDataTab/actions';
import {
  getAllDocuments,
  setDocumentsListStatus,
} from '../ManageDocuments/actions';

// Components
import EditOccupantModal from '../../components/EditOccupantModal';
import { clearIncompleteIdentificationPairs } from '../../components/EditOccupantModal/utils';
import {
  EditMoveInDateModal,
  EditMoveOutDateModal,
} from '../../components/EditMoveDateModal';
import { moveOutMessages } from '../../components/EditMoveDateModal/messages';
import UnitHistoryModal, {
  LINK_BUTTON_STYLE,
  UNIT_HISTORY_QUERY_KEY,
} from './UnitHistoryModal';
import type { UnitHistoryStay } from './UnitHistoryModal';
import unitHistoryMessages from './UnitHistoryModal/messages';
import EditPropertyMoveInDateModal from '../../components/EditPropertyMoveInDateModal';
import confirm from '../../components/ConfirmDialogModal';
import { editCompletedActivity } from '../../components/EditCompletedActivityModal';
import { editPendingActivity } from '../../components/EditPendingActivityModal';
import ElementWithPermissions from '../../components/ElementWithPermissions';
import {
  recordActivity,
  scheduleActivity,
} from '../../components/ActivityModal';
import ProfileLayout from '../../components/ProfileLayout';
import HouseholdNotifications from 'components/HouseholdNotifications';

// Containers
import Household from '../Household';
import ManageDocuments from '../ManageDocuments';
import Ledger from '../Ledger';
import WorkOrderTab from '../ManageWorkOrders/WorkOrderTab';
import ProspectInfoTab from '../PeopleProfile/ProspectInfoTab';
import LeaseSigning from '../LeaseSigning';
import LeaseDataTab from '../LeaseDataTab';
import HousingAssistanceVoucherTab from '../HousingAssistanceVoucher';
import CommercialLeaseDataTab from '../CommercialLeaseDataTab';

// Selectors
import {
  getAffordableRelationshipOptions,
  getAgedUpMinorApplicantTypeId,
  getApplicantTypeOptions,
  getCountryOptions,
  getMoveOutReasonsOptions,
  getNameSuffixOptions,
  getProspectAssigneesOptions,
  getMinorRelationshipOptions,
  getRelationshipOptions,
  getSelectedPropertyClassType,
  getStateOptions,
  getSpecialNeedsDesignationOptions,
  getHousingSituationOptions,
  getGuarantorOptions,
  getLiveInCaretakerOptions,
} from '../App/selectors';

// Types
import type {
  ActivityType,
  ContactType,
  DropdownOption,
  GlobalState,
  PaginationMeta,
  PetBreed,
  PetType,
  ReferralType,
  SelectOption,
  ProrateMethod,
} from '../App/types';
import {
  configureResidentApplicantAffordableTabsDisplay,
  resolveIsAffordableContext,
  resolveProfileTabPrograms,
  resolveShowQualificationTabs,
} from '../AffordableQualificationTab/Shared/utils';
import {
  AFFORDABLE_QUALIFICATION_STATUSES,
  getIsReceivingAssistance,
  getNonOptionalChargeFromQualifications,
} from '../../utils/affordable';

import type { Applicant } from '../CreateApplication/types';
import type { Unit } from '../ViewUnit/types';
import type { Activity } from '../ProspectProfile/types';

/* ResidentProfile Imports */
import LeaseApplicationTab from './LeaseApplicationTab';
import ScreeningTab from './ScreeningTab';
import noticeToVacate from './NoticeToVacateModal';
import ResidentMoveOut from './ResidentMoveOut';
import ResidentProfileDetails from './ResidentProfileDetails';
import ResidentTransferCard from './ResidentTransferCard';
import { TransferScreenContainer } from './ResidentTransfer';
import Snapshot from './Snapshot';
import SocketService, { base64FileDowload } from '../../services/socketService';
import ActivityTypeService from '../../services/activityTypeService';

import * as residentActions from './actions';
import {
  getMoveOutProratedTransactions,
  setProratedOtherAmount,
  setProratedRentAmount,
} from '../../utils/prorateCalculator';
import messages from './messages';
import {
  getApplicationStatusOptions,
  getApprovedResidents,
  getCurrentLease,
  getCurrentUser,
  getFinanciallyResponsiblePendingApplicants,
  getFinanciallyResponsibleResidents,
  getHouseholdPendingMembers,
  getHouseholdResidentMembers,
  getIsFutureLease,
  getLabelEndDate,
  getLateMethods,
  getMonthlyTransactions,
  getPreviousLeaseId,
  getRecompleteCertificationStatus,
  getSecurityDeposits,
  hasOpenTransferLease,
  hasOpenRenewalLease,
  isCurrentResident,
  isPriorHousehold,
  isPriorResident,
  isRenewalComplete,
  getScreeningLetterTypes,
  getMonthlyTransactionsForProrateCalculator,
  getFloorPlanAtMoveOut,
} from './selectors';
import type { HouseholdMember, NoticeToVacateForm } from './types';
import Tenant from '../Tenant';
import { createActivity } from '../../components/CreateActivityModal';
import ActivityContainer from '../../components/ActivityContents';
import {
  navigateToUrlWithSelectedPropertyId,
  getUrlWithSelectedPropertyId,
} from '../../utils/navigation-helpers';
import { DEPRECATED_MOVE_OUT_REASON_IDS } from './constants';
import {
  HUD_PROGRAM_NAME,
  LIHTC_PROGRAM_NAME,
  PH_PROGRAM_NAME,
  RD_PROGRAM_NAME,
} from '../GeneralAffordable/constants';
import { isGeneralAffordableNonHUD } from '../GeneralAffordable/utils';
import {
  checkIsSection236,
  getActiveResidentProfileTab,
  resolveSelectedResidentTab,
} from './utils';
import { LeaseDataTabProvider } from '../LeaseDataTab/context';
import {
  filterTrackedAutoFilledFields,
  mapApiAutoFilledLeaseChargeFields,
} from '../../constants/autoFilledLeaseChargeFields';

const GeneralAffordableNonHUD = LazyLoad(() =>
  import('../../containers/GeneralAffordable/GeneralAffordable.NonHUD'),
);
const GeneralAffordableHUD = LazyLoad(() =>
  import('../../containers/GeneralAffordable/GeneralAffordable.HUD'),
);
const GeneralAffordablePH = LazyLoad(() =>
  import('../GeneralAffordable/PH/PublicHousing'),
);
const GeneralAffordableRD = LazyLoad(() =>
  import('../../containers/GeneralAffordable/GeneralAffordable.RD'),
);

type AutoFilledLeaseChargeFields = {
  [key: string]: boolean,
};
type ModifiedAutoFilledLeaseChargeFields = {
  [key: string]: number,
};

type Props = {
  activeTab: string,
  currentUser: any,
  users: Array<DropdownOption>,
  locale: string,
  petTypes: Array<PetType>,
  petBreeds: Array<PetBreed>,
  contactTypes: Array<ContactType>,
  referralTypes: Array<ReferralType>,
  assignedTo: Object,
  applicationInfo: Object,
  applicationQualificationInfo: Object,
  prospectInfo: Object,
  ledgerBalance: string,
  subsidyBalance: string,
  monthsWithLateFees: number,
  householdMembers: Array<HouseholdMember>,
  residents: Array<any>,
  applicantCancellationReasons: Array<any>,
  houseHoldPendingMembers: Array<any>,
  financiallyResponsibleHousehold: Array<any>,
  frPendingApplicants: Array<HouseholdMember>,
  activityTypes: Array<ActivityType>,
  pendingActivities: Array<Activity>,
  completedActivities: Array<Activity>,
  completedActivitiesMeta: PaginationMeta,
  allActivities: Array<Activity>,
  allActivitiesMeta: PaginationMeta,
  columnOrder: Object,
  states: Array<SelectOption>,
  moveOutReasons: Array<SelectOption>,
  transferMoveOutReasonOptions: Array<{ value: string, text: string }>,
  labelEndDate: any,
  isFutureLease: boolean,
  unit?: Unit,
  propertyClassType: string,
  suffixes: Array<Object>,
  countries: Array<Object>,
  submittingRenewal: boolean,
  selectedProperty: ?Object,
  transferInformation?: Object,
  leases: Array<Object>,
  leaseSignatureStatuses: Object,
  affordableQualifications: Array<Object>,
  affordableException: Object,
  affordableSetup: Object,
  householdAffordableDetails: ?Object,
  firstUnitMoveInAfterPriorResident: any,
  monthsWithNSFFees: number,
  moveInApplyFullAmountToProRate: boolean,
  moveOutProratedRentMethodId: string,
  deprecated_moveOutProratedRentMethodId: string,
  prorateMethods: Array<ProrateMethod>,
  specialNeedsDesignations: Array<SelectOption>,
  fpNonOptionalCharges: Array<Object>,
  fasComplete: boolean,
  propertyPaymentProvider: string,
  propertyHasHUD: boolean,
  housingSituations: Array<SelectOption>,
  guarantorOptions: Object,
  liveInCaretakerOptions: Object,
  isMoveOutRequestProcessing: boolean,
  flags?: Object,
  // ENG-29435: affordable programs on the target floor plan of an in-progress unit transfer.
  // Injected by withTransferTargetPrograms (react-query), not held in the redux slice.
  transferTargetPrograms: Array<Object>,
};
type InjectedProps = {
  intl: any,
  actions: Object,
  residentId: string,
  residentInfo: Object,
  history: Object,
  currentLease: Object,
  leaseTerms: Array<any>,
  lateMethods: Array<any>,
  leaseRentPercentage: string,
  securityDeposits: Array<any>,
  basicLeaseFees: Array<any>,
  monthlyTransactions: Array<any>,
  selectedMonthlyOption: string,
  match: Object,
  applicationStatuses: Array<any>,
  applicants: any,
  screening: string,
  previousLeaseId: string,
  hasOpenTransfer: boolean,
  hasOpenRenewal: boolean,
  isResident: boolean,
  isPriorHousehold: boolean,
  isPriorResident: boolean,
  isRenewalComplete: boolean,
  openFiscalPeriod: Object,
  isFirstFiscalPeriod: boolean,
  affordableRelationships: Array<Object>,
  relationshipOptions: Array<Object>,
  minorRelationshipOptions: Array<Object>,
  applicantTypeOptions: Array<Object>,
  agedUpMinorApplicantTypeId: ?string,
  applicationDecisionStatus: any,
  reCompleteCertificationStarted: ?boolean,
  deletedApplicants: Array<Object>,
  screeningLetterTypes: Array<Object>,
};

type State = {
  activityHistoryCurrentPage: number,
  activityHistoryField: string,
  activityHistoryLimit: number,
  activityHistoryOrder: string,
  actualMoveOutDate: any,
  actualTransferDate: any,
  complianceApprovalId: string,
  editMode: boolean,
  editModeProspectInfo: boolean,
  editModeRequiresNote: boolean,
  key: string,
  hasHouseholdRentableItems: boolean,
  missingInitialCert: boolean,
  modal: ?string,
  monthlyTransactions: Array<any>,
  occupantToEdit: string,
  // ENG-30200 AC#1 -- the type ('adult' | 'minor') chosen in
  // AddHouseholdMemberModal's chooser step, threaded through to the Add
  // Mini-Profile (see newOccupantInfo below). '' means no selection has
  // been made (or committed) yet.
  newMemberType: string,
  processMoveOut: boolean,
  processTransfer: boolean,
  recertEnabled: boolean,
  reCompleteCertificationStarted: ?boolean,
  retrievedPendingActivities: boolean,
  showRecert: boolean,
  householdId: string,
  activityTableUpdateTrigger: boolean,
  modifiedAutoFilledLeaseChargeFields: ModifiedAutoFilledLeaseChargeFields,
  autoFilledLeaseChargeFields: AutoFilledLeaseChargeFields,
  /** The stay the Edit Move-In Date modal corrects when opened from the Unit History. */
  moveInStay: ?UnitHistoryStay,
};

export class ResidentProfile extends Component<Props & InjectedProps, State> {
  constructor(props: Props & InjectedProps) {
    super(props);
    this.state = {
      activityHistoryCurrentCount: 0,
      activityHistoryCurrentPage: 1,
      activityHistoryField: 'dateTime',
      activityHistoryLimit: 10,
      activityHistoryOrder: 'DESC',
      activityHistoryPageCount: 0,
      activityHistoryTotalCount: 0,
      actualMoveOutDate: moment(),
      actualTransferDate: moment(),
      complianceApprovalId: '',
      editMode: false,
      editModeProspectInfo: false,
      editModeRequiresNote: false,
      isFinalized: false,
      missingInitialCert: false,
      modal: null,
      moveInStay: null,
      monthlyTransactions: [],
      monthlyTransactionsForProrateCalculator: [],
      occupantToEdit: '',
      newMemberType: '',
      processMoveOut: false,
      processTransfer: false,
      recertEnabled: false,
      reCompleteCertificationStarted: false,
      retrievedPendingActivities: false,
      showRecert: false,
      hasHouseholdRentableItems: false,
      hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate: false,
      refetchHouseholdRentableItemsTrigger: 0,
      key: this.props.activeTab,
      householdId: '',
      activityTableUpdateTrigger: false, // tells activity table to re-query activities
      modifiedAutoFilledLeaseChargeFields: {},
      autoFilledLeaseChargeFields: {},
    };
  }

  componentDidMount() {
    const { residentId } = this.props;
    const hasValidResidentId = isValidResidentId(residentId);

    this.props.actions.getAllActivityTypes();
    this.props.actions.getAllAssignees();
    this.props.actions.getAllPetTypes();
    this.props.actions.getAllPetBreeds();
    this.props.actions.getAllContactTypes();
    this.props.actions.getAllReferralTypes();
    this.props.actions.getAllStates();
    this.props.actions.getAllCountries();
    this.props.actions.getAllMoveOutReasons();
    this.props.actions.getAllHousingSituations();
    if (hasValidResidentId) {
      this.props.actions.getOneResident(residentId);
      this.props.actions.getResidentHousehold(residentId);
      this.props.actions.getFirstUnitMoveInAfterPriorResident(residentId);
      this.props.actions.getHouseholdUnitPrograms(residentId);
    }
    this.props.actions.updateColumnsSortValue('dateTime', 'descending');
    this.props.actions.getAllApplicationStatus();
    this.props.actions.getAllNameSuffixes();
    this.props.actions.getAllAffordableRelationships();
    this.props.actions.getAllApplicantTypes();
    this.props.actions.getAllRelationships();
    this.props.actions.getAllApplicationDecisionStatus();
    this.props.actions.getAllApplicantCancellationReasons();
    this.props.actions.getAllProrateMethods();
    this.props.actions.getAllMoveOutProrateMethods();
    this.props.actions.getAllSpecialNeedsDesignations();
    this.props.actions.updateSelectedProperty();

    if (SocketService && SocketService.socket) {
      if (SocketService.socket) {
        SocketService.socket.off('renewalProrateLedgerAdjustment.success');
        SocketService.socket.off('renewalProrateDocument.success');
      }
      SocketService.socket.on(
        'renewalProrateLedgerAdjustment.success',
        this.onRenewalProrateAdjustmentsPosted,
      );
      SocketService.socket.on(
        'renewalProrateDocument.success',
        this.onRenewalProrateDocumentCreated,
      );
      SocketService.socket.on(
        'renewalProrateDocument.error',
        this.onRenewalProrateDocumentError,
      );
    }
  }

  componentWillUnmount() {
    this.props.actions.cleanLoadedResident();
    if (SocketService.socket) {
      SocketService.socket.off('renewalProrateLedgerAdjustment.success');
      SocketService.socket.off('renewalProrateDocument.success');
      SocketService.socket.off('renewalProrateDocument.error');
    }
  }

  componentDidUpdate(prevProps: Object, prevState: Object) {
    // When the route path changes but residentId stays the same (e.g., after
    // move-out navigating from /resident/:id to /prior-resident/:id), React
    // batches both push() calls and skips the intermediate '/' route, so the
    // component never unmounts. Detect this by checking if match.path changed
    // while residentId is the same, then reset local state and re-fetch.
    const prevMatchPath = prevProps?.match?.path ?? '';
    const currentMatchPath = this.props?.match?.path ?? '';
    const { residentId } = this.props;
    const hasValidResidentId = isValidResidentId(residentId);
    if (
      prevMatchPath !== currentMatchPath &&
      prevProps.residentId === residentId &&
      hasValidResidentId
    ) {
      // Reset local state that may still be active from the previous route
      // (e.g. processMoveOut stays true if component isn't remounted)
      this.setState({
        processMoveOut: false,
        processTransfer: false,
        retrievedPendingActivities: false,
      });
      this.props.actions.getOneResident(residentId);
      this.props.actions.getResidentHousehold(residentId);
      this.props.actions.getFirstUnitMoveInAfterPriorResident(residentId);
      this.props.actions.getHouseholdUnitPrograms(residentId);
    }

    if (
      this.state.retrievedPendingActivities === false &&
      this.props.prospectInfo.id &&
      this.props.applicationInfo
    ) {
      this.setState({ ...this.state, retrievedPendingActivities: true });
      this.props.actions.getProspectPendingActivities(
        this.props.prospectInfo.id,
      );
      this.refreshActivities();
    }

    const prevPropsleases = pathOr([], ['leases'], prevProps);
    const prevPropsLeaseId =
      prevPropsleases.length > 0 && pathOr(null, ['id'], prevPropsleases[0]);
    const prevPropsDesiredSignatureMethod =
      prevPropsleases.length > 0 &&
      pathOr(null, ['desiredSignatureMethod'], prevPropsleases[0]);
    // $FlowFixMe
    const leases = pathOr([], ['leases'], this.props);
    const newestLease = leases.length > 0 ? leases[0] : {};
    const desiredSignatureMethod = pathOr(
      null,
      ['desiredSignatureMethod'],
      newestLease,
    );
    if (
      (prevPropsLeaseId !== newestLease.id ||
        prevPropsDesiredSignatureMethod !== desiredSignatureMethod) &&
      desiredSignatureMethod === ELECTRONIC_SIGNING_METHOD
    ) {
      this.props.actions.getSignatureStatuses(newestLease.id);
    }

    const previousHouseholdId = pathOr(
      null,
      ['residentInfo', 'householdId'],
      prevProps,
    );
    const currentHouseholdId = pathOr(
      null,
      ['residentInfo', 'householdId'],
      this.props,
    );
    const hasCam = pathOr(
      false,
      ['applicationInfo', 'property', 'isCAMActive'],
      this.props,
    );
    if (
      hasCam &&
      previousHouseholdId !== currentHouseholdId &&
      !isNil(currentHouseholdId)
    ) {
      this.props.actions.fetchCamPools(currentHouseholdId);
    }

    if (
      previousHouseholdId !== currentHouseholdId &&
      !isNil(currentHouseholdId)
    ) {
      this.props.actions.getStartOfMonthMoveOutProratedTransactons(
        currentHouseholdId,
      );
    }

    const { activeTab } = this.props;
    if (
      activeTab !== prevProps.activeTab &&
      activeTab &&
      activeTab !== this.state.key
    ) {
      this.setState((prevState) => ({ ...prevState, key: activeTab }));
    }

    const leaseIsLocked = newestLease?.isLocked;
    const leaseChanged = !_.isEqual(this.props.leases, prevPropsleases);

    if (
      this.props.flags.automatedLeaseChargesP1 &&
      !leaseIsLocked &&
      leaseChanged
    ) {
      this.setState((prevState) => ({
        ...prevState,
        autoFilledLeaseChargeFields: filterTrackedAutoFilledFields({
          ...prevState.autoFilledLeaseChargeFields,
          ...mapApiAutoFilledLeaseChargeFields(
            newestLease?.autoFilledLeaseChargeFields,
          ),
        }),
      }));
    }

    this.props.socketFailed &&
      this.props.fasComplete &&
      this.props.history.replace('?');
  }

  onRenewalProrateAdjustmentsPosted = () => {
    const customerId = pathOr(
      null,
      ['residentInfo', 'residentCustomer', 'customerId'],
      this.props,
    );
    if (!isNil(customerId)) {
      this.props.actions.refreshLedger(customerId, true);
    }
  };

  onRenewalProrateDocumentError = (data) => {
    this.props.actions.promptToaster({
      type: 'error',
      title: this.props.intl.formatMessage(
        messages.errorRenewalProrateDocumentCreatedTitle,
      ),
      message: this.props.intl.formatMessage(
        messages.errorRenewalProrateDocumentCreated,
      ),
    });
  };

  onRenewalProrateDocumentCreated = (data) => {
    this.props.actions.promptToaster({
      type: 'success',
      title: this.props.intl.formatMessage(
        messages.successRenewalProrateDocumentCreatedTitle,
      ),
      message: this.props.intl.formatMessage(
        messages.successRenewalProrateDocumentCreated,
      ),
    });

    // Refresh documents tab
    const applicationId = pathOr(
      '',
      ['residentInfo', 'household', 'currentApplicationId'],
      this.props,
    );
    if (!isNil(applicationId)) {
      this.props.actions.getAllDocuments(applicationId);
    }

    // Download document
    const fileName = pathOr(null, ['file', 'name'], data);
    const fileData = pathOr(null, ['file', 'data'], data);
    if (!isNil(fileName) && !isNil(fileData)) {
      base64FileDowload(fileData, fileName, 'application/pdf');
    }
  };

  static getDerivedStateFromProps(props: any, state: any) {
    const queryString = parse(
      pathOr('', ['location', 'search'], props).replace('?', ''),
    );
    const selectedPropertyId = pathOr('', ['selectedProperty', 'id'], props);
    const { propertyId: queryPropertyId } = queryString;
    const userProperties = pathOr(
      [],
      ['currentUser', 'user', 'properties'],
      props,
    );
    if (
      queryPropertyId &&
      queryPropertyId !== selectedPropertyId &&
      userProperties.some((p) => p.id === queryPropertyId)
    ) {
      const newProperty = userProperties.find((p) => p.id === queryPropertyId);
      props.actions.selectProperty(newProperty);
    } else if (!selectedPropertyId) {
      props.history.push('/');
    }

    if (state.monthlyTransactions !== props.monthlyTransactions) {
      return {
        ...state,
        monthlyTransactions: props.monthlyTransactions,
      };
    }

    if (
      state.monthlyTransactionsForProrateCalculator !==
      props.monthlyTransactionsForProrateCalculator
    ) {
      return {
        ...state,
        monthlyTransactionsForProrateCalculator:
          props.monthlyTransactionsForProrateCalculator,
      };
    }

    return null;
  }

  onProcessMoveOutClick = () => {
    this.setState({
      ...this.state,
      processMoveOut: true,
      actualMoveOutDate: moment(),
    });
  };

  onCancelProcessMoveOutClick = () => {
    this.setState({ ...this.state, processMoveOut: false });
  };

  moveOutDateStatus = (formSyncErrors: Object) => {
    return {
      statusText:
        Object.keys(formSyncErrors).includes('moveOutDate') &&
        typeof formSyncErrors.moveOutDate !== 'undefined'
          ? 'Not Confirmed'
          : 'Confirmed',
      valid:
        Object.keys(formSyncErrors).includes('moveOutDate') &&
        typeof formSyncErrors.moveOutDate !== 'undefined'
          ? 'status status-ball status-red'
          : 'status status-ball status-green',
    };
  };

  handleChecklistOptionChange = (
    applicant: Object,
    item: Object,
    type: string,
    { target: { value } }: Object,
  ) => {
    const checklistChanges = {
      applicationId: applicant.applicationId,
      applicantChecklistId: applicant.applicantChecklistId,
      checklistItemId: item.checklistItemId,
      checklistItemOptionId: type === 'option' ? value : item.selectedOption,
      applicantChecklistDecisionId: type === 'decision' ? value : null,
    };
    return this.props.actions.updateApplicantChecklist(checklistChanges);
  };

  handleMultipleChecklistOptionsChange = (applicant, items, callback) => {
    const checklistChanges = items.map(
      ({ checklistItemId, applicantChecklistDecisionId }) => ({
        checklistItemId,
        applicantChecklistDecisionId: applicantChecklistDecisionId ?? null,
      }),
    );

    return this.props.actions.updateMultipleApplicantChecklistFields(
      checklistChanges,
      this.props.currentApplicationId,
      applicant.applicantChecklistId,
      callback,
    );
  };

  confirmMoveOutModal = (handleSubmit: Function) => {
    const isAllCommercial =
      this.props.selectedProperty.hasCommercialFloorPlans === 'ALL';
    confirm(
      isAllCommercial
        ? this.props.intl.formatMessage(messages.confirmMoveOutTenant)
        : this.props.intl.formatMessage(messages.confirmMoveOut),
      {
        intl: this.props.intl,
      },
    ).then(
      () => {
        handleSubmit();
      },
      () => {},
    );
  };

  approveApplicant = ({ id, applicationId }: any, cancelFunc: ?Function) => {
    confirm(
      `Are you sure you want approve this applicant
    and move him in as a resident of this household?`,
      { cancelFunc },
    ).then(() => this.props.actions.approveApplicant(id, applicationId));
  };
  cancelApplicant = ({ id, applicationId }: any, cancelReasonId: string) => {
    confirm(
      "Are you sure you want deny this applicant and cancel it's application?", // eslint-disable-line
    ).then(() =>
      this.props.actions.cancelApplicant(id, applicationId, cancelReasonId),
    );
  };

  handleSubmitMoveOut = (moveOutInfo: Object) => {
    const newProRatedTransactions = moveOutInfo.proRatedTransactions.map(
      (transaction) => {
        delete transaction.monthlyTransactionId;
        return transaction;
      },
    );
    const newMoveOutInfo = {
      ...moveOutInfo,
      proRatedTransactions: newProRatedTransactions,
    };
    this.props.actions.moveOutResident(newMoveOutInfo, this.props.residentId);
  };

  checkUserPermissions = (testScope: Array<string>) => {
    const userScopes = this.props.currentUser.permissions.map(
      (permission) => permission.scope,
    );
    return intersection(testScope, userScopes).length > 0;
  };

  handleEditHousehold = () => {
    const url = `/resident/${this.props.residentId}/household`;
    navigateToUrlWithSelectedPropertyId(url);
  };

  handleRenewLease = () => {
    const url = `/generate-renewal-offer/${this.props.residentId}`;
    navigateToUrlWithSelectedPropertyId(url);
  };

  handleTransfer = () => {
    try {
      const selectedPropertyId = this.props.selectedProperty?.id ?? '';
      const { residentInfo, unit } = this.props ?? {};
      const applicationId = residentInfo?.household?.currentApplicationId ?? '';
      const applicantId = residentInfo?.applicant?.id ?? '';
      const residentId = residentInfo?.id ?? '';
      const currentUnitNumber = unit?.number ?? null;
      const isCurrentUnitConventional = !(
        unit?.floorPlan?.isAffordable ?? false
      );
      const prospectInfo = this.props?.prospectInfo ?? {};
      const applicationInfo = this.props?.applicationInfo ?? {};
      const currentUnitBuilding = this.props.unit?.building?.name ?? null;
      const currentUnitHasLIHTC = (
        unit?.floorPlan?.floorPlanAffordablePrograms ?? []
      ).some((fpPap) =>
        fpPap.propertyAffordableProgram?.masterAffordableProgram?.name?.includes(
          LIHTC_PROGRAM_NAME,
        ),
      );
      Object.assign(applicationInfo, { prospectInfo });
      localStorage.setItem(
        'previous-applicant-id',
        JSON.stringify(applicationInfo) + '',
      );
      let url = `/property/${selectedPropertyId}/manage-unit-availability?`;
      url = url
        .concat(`applicantId=${applicantId}`)
        .concat(`&residentId=${residentId}`)
        .concat(`&applicationId=${applicationId}`)
        .concat(`&fromUnit=${currentUnitNumber}`)
        .concat(`&isCurrentUnitConventional=${isCurrentUnitConventional}`)
        .concat(`&fromBuilding=${currentUnitBuilding}`)
        .concat(`&isCurrentUnitLIHTC=${currentUnitHasLIHTC}`)
        .concat('&isTransfer=true');
      this.props.history.push(url);
    } catch (e) {
      /* empty */
    }
  };

  handleTransferCancel = () => {
    this.setState({
      ...this.state,
      processTransfer: false,
      processMoveOut: false,
      actualTransferDate: moment(),
    });
  };

  onCompleteTransferClick = () => {
    this.setState({
      ...this.state,
      processTransfer: true,
      processMoveOut: false,
      actualTransferDate: moment(),
    });
  };

  handleAssignedToChange = (event: Object, newUserId: string) => {
    this.props.actions.assignApplication(
      {
        ...this.props.applicationInfo,
        assignedToId: newUserId,
      },
      () => this.refreshActivityTable(),
    );
  };

  handleDoNotRenewClick = (): void => {
    const {
      applicationInfo: { applicants },
      residentInfo: {
        household: { doNotRenew },
      },
      actions: { updateDoNotRenew },
    } = this.props;
    const updatedCustomers = applicants.reduce((customers, applicant) => {
      const customerId = pathOr(
        null,
        ['applicantCustomer', 'customer', 'id'],
        applicant,
      );
      if (customerId) {
        customers.push({ [customerId]: !doNotRenew });
      }
      return customers;
    }, []);
    updateDoNotRenew(updatedCustomers, !doNotRenew);
  };

  handleFasReady = (): void => {
    const {
      residentInfo: {
        lease: { id, fasReady },
      },
      actions: { updateFasReady },
    } = this.props;
    updateFasReady(id, !fasReady);
  };

  handleUnderEvictionClick = (): void => {
    const {
      applicationInfo: { applicants },
      residentInfo: {
        household: { underEviction },
      },
      currentLease,
    } = this.props;
    const updatedCustomers = applicants.reduce((customers, applicant) => {
      const customerId = pathOr(
        null,
        ['applicantCustomer', 'customer', 'id'],
        applicant,
      );
      if (customerId) {
        customers.push({ [customerId]: !underEviction });
      }
      return customers;
    }, []);

    this.props.actions.updateUnderEviction(
      updatedCustomers,
      !underEviction,
      currentLease.id,
    );
  };
  handleAutoEmailMonthlyInvoiceClick = (): void => {
    const {
      residentInfo: {
        household: { autoEmailMonthlyInvoices, id },
      },
    } = this.props;
    this.props.actions.updateAutoEmailMonthlyInvoice(
      id,
      !autoEmailMonthlyInvoices,
    );
  };

  handleNoticeToVacateSubmit = (values: NoticeToVacateForm) => {
    const { states } = this.props;
    const { toDelete, ...ntv } = values;
    const isDefault = (val) => val === 'default' || val === '';
    const isNilOrDefault = either(isNil, isDefault);
    // $FlowFixMe
    const filteredNtv = reject(isNilOrDefault, ntv);
    const state = states.find(
      (state: SelectOption) => filteredNtv.state === state.value,
    );
    const ntvToSave =
      values.noForwardingAddress === true
        ? {
            ...filteredNtv,
            street: null,
            city: null,
            state: null,
            stateName: null,
            zipCode: null,
          }
        : {
            ...filteredNtv,
            state: state ? state.value : null,
            stateName: state ? state.text : null,
          };

    if (toDelete) {
      this.props.actions.deleteNoticeToVacate(
        ntvToSave,
        this.refreshActivityTable,
      );
    } else {
      this.props.actions.saveNoticeToVacate(
        ntvToSave,
        this.refreshActivityTable,
      );
    }
  };

  handleNoticeToVacateClick = async (isFromFAS = false) => {
    const { residentId, isResident, flags } = this.props;
    const forwardingAddressRequired = !isResident;
    const backdateMoveOutDateMoreThan30DaysFlag =
      flags?.backdateMoveOutDateMoreThan30Days ?? false;
    const naurFlag = flags?.naur ?? false;
    const unitTransfersFlag = flags?.unitTransfers ?? false;
    const selectedPropertyId = this.props.selectedProperty?.id;
    const unitId = this.props.unit?.id;

    await noticeToVacate(
      this.getReduxStore(),
      this.props.intl,
      this.props.propertyHasHUD,
      this.props.propertyClassType,
      this.props.unit,
      this.props.states,
      forwardingAddressRequired,
      this.props.moveOutReasons,
      this.props.housingSituations,
      this.props.applicationInfo.property.turnMoveOutDays,
      this.props.residentInfo,
      this.handleNoticeToVacateSubmit,
      this.props.openFiscalPeriod,
      this.props.leases,
      isFromFAS,
      this.props.financiallyResponsibleHousehold,
      backdateMoveOutDateMoreThan30DaysFlag,
      naurFlag,
      selectedPropertyId,
      unitId,
      unitTransfersFlag,
    );

    this.props.actions.submit('NoticeToVacate');
    if (forwardingAddressRequired && isFromFAS) {
      navigateToUrlWithSelectedPropertyId(
        `/prior-resident/${residentId}/final-account-statement`,
      );
    }
  };

  onActivityHistoryOrder = (field: string) => {
    const columnOrder = this.props.columnOrder[field];
    const order = columnOrder === 'ascending' ? 'DESC' : 'ASC';
    const icon =
      columnOrder === 'sortable'
        ? 'ascending'
        : columnOrder === 'ascending'
        ? 'descending'
        : 'ascending';
    this.props.actions.updateColumnsSortValue(field, icon);
    this.setState({ activityHistoryField: field, activityHistoryOrder: order });
    this.props.actions.getProspectAllActivities(
      this.props.prospectInfo.id,
      this.state.activityHistoryCurrentPage,
      this.state.activityHistoryLimit,
      field,
      order,
    );
    this.props.actions.getProspectCompletedActivities(
      this.props.prospectInfo.id,
      this.state.activityHistoryCurrentPage,
      this.state.activityHistoryLimit,
      field,
      order,
    );
  };

  refreshActivities() {
    if (!!this.props.prospectInfo.id) {
      this.props.actions.getProspectAllActivities(
        this.props.prospectInfo.id,
        this.state.activityHistoryCurrentPage,
        this.state.activityHistoryLimit,
        this.state.activityHistoryField,
        this.state.activityHistoryOrder,
      );
      this.props.actions.getProspectCompletedActivities(
        this.props.prospectInfo.id,
        this.state.activityHistoryCurrentPage,
        this.state.activityHistoryLimit,
        this.state.activityHistoryField,
        this.state.activityHistoryOrder,
      );
    }
  }

  onActivityHistoryPageChange(pageNumber: number) {
    this.setState({ activityHistoryCurrentPage: pageNumber }, () =>
      this.refreshActivities(),
    );
  }

  getReduxStore = () => this.context?.store || store;

  onRecordActivityClick = async () => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList(
      this.props,
    );
    const users = this.props.users;
    /* istanbul ignore else */
    if (this.props.prospectInfo.id != null) {
      const data = await recordActivity(
        this.getReduxStore(),
        this.props.intl,
        this.props.prospectInfo,
        activityTypesList,
        users,
        'resident',
        this.props.residentId,
      );
      this.props.actions.createProspectActivity(this.getActivityData(data));
      this.setState({ activityHistoryCurrentPage: 1 });
    }
  };

  onScheduleActivityClick = async () => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList(
      this.props,
    );
    const users = this.props.users;
    /* istanbul ignore else */
    if (this.props.prospectInfo.id != null) {
      const data: Activity = await scheduleActivity(
        this.getReduxStore(),
        this.props.intl,
        this.props.prospectInfo,
        activityTypesList,
        users,
        'resident',
        this.props.residentId,
      );
      this.props.actions.createProspectActivity(this.getActivityData(data));
      this.setState({ activityHistoryCurrentPage: 1 });
    }
  };

  onCreateActivityClick = async (refresh?: Function) => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList(
      this.props,
    );
    const users = this.props.users;
    if (this.props.prospectInfo.id != null) {
      const data: Activity = await createActivity({
        store: this.getReduxStore(),
        intl: this.props.intl,
        prospect: this.props.prospectInfo,
        activityTypesList: activityTypesList,
        assigneeList: users,
        stage: 'resident',
        urlId: this.props.residentId,
      });
      this.props.actions.createProspectActivity(
        this.getActivityData(data),
        refresh,
      );
      this.setState({ activityHistoryCurrentPage: 1 });
    }
  };

  onEditActivity = (activity: Activity, refresh?: Function) => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList(
      this.props,
    );
    const users = this.props.users;
    const stageInfo = {
      currentStage: 'resident',
      urlId: this.props.residentId,
    };

    const editPromise = activity.activityCompletionStatusId
      ? editCompletedActivity(
          this.getReduxStore(),
          this.props.intl,
          activity,
          stageInfo,
        )
      : editPendingActivity(
          this.getReduxStore(),
          this.props.intl,
          activity,
          activityTypesList,
          users,
          this.props.prospectInfo,
          stageInfo,
        );

    editPromise.then((data: Activity) => {
      if (data.saveAndClose) {
        this.props.actions.updateProspectActivity(data, refresh);
      } else if (data.saveAndAddNew) {
        this.props.actions.updateProspectActivity(data, refresh);
        this.onCreateActivityClick(this.forceRefreshActivityTable);
      } else {
        this.props.actions.deleteProspectActivity(data, refresh);
      }
      this.setState({ activityHistoryCurrentPage: 1 });
    });
  };

  handleEditApplicationForm = (applicant: Applicant) => {
    const isProspect = () =>
      and(
        not(isNil(applicant)),
        pathEq(['applicantCustomer', 'isProspect'], true)(applicant),
      );

    const isFinanciallyResponsible = () =>
      and(
        and(
          not(isNil(applicant)),
          not(isNil(path(['applicantCustomer'], applicant))),
        ),
        and(
          not(isProspect()),
          pathEq(['applicantType', 'financiallyResponsible'], true)(applicant),
        ),
      );

    const isNotFinanciallyResponsible = () =>
      and(
        and(
          not(isNil(applicant)),
          not(isNil(path(['applicantCustomer'], applicant))),
        ),
        and(not(isProspect()), not(isFinanciallyResponsible())),
      );

    if (isProspect()) {
      window.location.assign(
        getUrlWithSelectedPropertyId(
          `/primary-form/${applicant.applicationId}/${applicant.id}`,
        ),
      );
    }
    if (isFinanciallyResponsible()) {
      window.location.assign(
        getUrlWithSelectedPropertyId(
          `/non-primary-form/${applicant.applicationId}/${applicant.id}`,
        ),
      );
    }
    if (isNotFinanciallyResponsible()) {
      window.location.assign(
        getUrlWithSelectedPropertyId(
          `/shortApplication/${applicant.applicationId}/${applicant.id}`,
        ),
      );
    }
  };

  /**
   * TODO: both `handleRenewal` is a higher order function that is taking a
   * callback because we want a toastr and to refresh the data via sagas.
   * Move this into the LeaseDataTab so that we don't need this craziness here.
   *
   *
   * -> Addressed on commmercial lease data tab
   */
  handleRenewal = (updateCurrentLeaseCb: Function) => () => {
    const { residentId, previousLeaseId } = this.props;
    this.props.actions.renewLease(
      residentId,
      previousLeaseId,
      {},
      updateCurrentLeaseCb,
    );
  };

  /**TODO:
   * Refreshing the list of leases is being done by refreshing the whole resident
   * We want to eventually replace this one function to retrieve only that
   */
  handleRefreshLeases = () => {
    const { residentId } = this.props;
    this.props.actions.getOneResident(residentId);
  };

  // MiniProfileBridge only invalidates React Query caches — the visible
  // Household table still reads from Redux (getHouseholdResidentMembers), so
  // both Save and Cancel need the same refresh the legacy occupant-edit
  // modal already triggers on save.
  handleMiniProfileRefresh = () => {
    const { residentId } = this.props;
    this.props.actions.getOneResident(residentId);
    this.props.actions.getResidentHousehold(residentId);
  };

  // Cancel refreshes too, so an edit made elsewhere on the page while the
  // modal was open is not left stale on screen. Skipped after a save, where
  // onSaveSuccess (handleMiniProfileRefresh) has already run the same pair --
  // options is undefined here when MUI's own dismiss calls this.
  handleMiniProfileClose = (options?: { afterSave?: boolean }) => {
    if (this.isEditHouseholdMemberDatesEnabled() && !options?.afterSave) {
      this.handleMiniProfileRefresh();
    }
    this.resetModal();
  };

  handleCommercialLeaseBasicsSubmit = (
    leaseId: string,
    values: Object,
    onSettled?: Function,
  ) => {
    const {
      actions: { saveLeaseDataTabResident },
      residentId,
      unit,
    } = this.props;
    const leaseData = {
      ...mapCommercialLeaseBasicsToLeaseData(values),
      unitId: unit.id,
    };

    saveLeaseDataTabResident(
      leaseId,
      leaseData,
      residentId,
      undefined,
      onSettled,
    );
  };

  handleAutoFilledFieldChange = (fieldName: string, value: number) => {
    this.setState((prevState) => {
      const { modifiedAutoFilledLeaseChargeFields } = prevState;
      const updatedFields = {
        ...modifiedAutoFilledLeaseChargeFields,
        [fieldName]: value,
      };
      const autoFilledFields = {
        ...prevState.autoFilledLeaseChargeFields,
        [fieldName]: false,
      };
      return {
        modifiedAutoFilledLeaseChargeFields: updatedFields,
        autoFilledLeaseChargeFields: autoFilledFields,
      };
    });
  };

  resetProratedRentAutoFilledChangeStatus = () => {
    this.setState((prevState) => {
      const { modifiedAutoFilledLeaseChargeFields } = prevState;
      const updatedFields = { ...modifiedAutoFilledLeaseChargeFields };
      delete updatedFields.proratedRent;
      return {
        modifiedAutoFilledLeaseChargeFields: updatedFields,
        autoFilledLeaseChargeFields: {
          ...prevState.autoFilledLeaseChargeFields,
          proratedRent: true,
          proratedOther: true,
        },
      };
    });
  };

  leaseDataHandleSubmit = (
    values: Object,
    refreshFn?: Function,
    onSettled?: Function,
  ) => {
    const { householdMembers } = this.props;
    const nonFRAdults = householdMembers.filter(
      // $FlowFixMe
      ({ type, financiallyResponsible, status }) =>
        type === 'adult' && !financiallyResponsible && status !== 'Prior',
    );
    const desiredSignatureMethod =
      values.desiredSignatureMethod === ELECTRONIC_SIGNING_METHOD &&
      nonFRAdults.length > 0
        ? 'default'
        : values.desiredSignatureMethod;
    const formData = mapFormValuesLeaseData({
      ...values,
      desiredSignatureMethod,
    });

    const refreshFunction = () => {
      refreshFn?.();
      this.refreshActivityTable();
    };

    this.props.actions.saveLeaseDataTabResident(
      formData.id,
      formData,
      this.props.residentId,
      refreshFunction,
      onSettled,
    );
    return values;
  };

  validateRequired = (value: any) =>
    not(isEmpty(value)) && not(isNil(value)) && !equals('default', value);

  leaseDataHandleGenerateLease = async (values: any) => {
    const data = mapFormValuesLeaseData(values);
    const { currentLease } = this.props;
    await this.props.actions.generateFutureLeaseDocument({
      leaseId: data.id,
      lease: data,
      residentId: this.props.residentId,
      transfer: currentLease.isTransfer,
    });
    return values;
  };

  removeLease = (
    applicationId: string,
    leaseId: string,
    residentId: string,
    refreshFn?: Function,
  ) => {
    const prospectId = pathOr('', ['prospectInfo', 'id'], this.props);
    this.props.actions.removeLease(
      applicationId,
      leaseId,
      residentId,
      prospectId,
      refreshFn,
    );
  };

  getActivityData(data: Object) {
    return Object.assign(data, {
      customerStatus: this.props.isResident
        ? 'Current Resident'
        : 'Prior Resident',
    });
  }

  // TODO: scrub of affordable values when 59 is live
  updateApplication = (changes: any) => {
    const { actions, applicationInfo, residentId } = this.props;
    const propertyId = pathOr('', ['property', 'id'], applicationInfo);
    const HHmoveInInfoManual = pathOr(
      false,
      ['HHmoveInInfoManual'],
      applicationInfo,
    );
    const { HHincomeAtMoveIn, HHnumOccupantsAtMoveIn } = changes;
    if ((HHincomeAtMoveIn || HHnumOccupantsAtMoveIn) && !HHmoveInInfoManual) {
      changes = {
        ...changes,
        HHmoveInInfoManual: true,
      };
    }
    const pickedFields = pick(
      [
        'id',
        'unitTypeId',
        'prospectId',
        'propertyId',
        'notes',
        'vehicleCount',
        'applicationStatusId',
        'updatedById',
        'propertyClassId',
        'adults',
        'minors',
        'pets',
        'fees',
        'affordableQualifyingChecklist',
        'publicHousingAuthority',
        'hudIncomeLimitId',
        'affordableDocuments',
        'reCompleteCertificationStarted',
        'householdUtilityAllowanceId',
        'voucherEffectiveDate',
        'rentalAssistanceSourceId',
        'complianceApprovalId',
        'setAsideProgramId',
      ],
      applicationInfo,
    );

    const toScrubArray = ['HHincomeAtMoveIn', 'HHnumOccupantsAtMoveIn'].reduce(
      (scrubArr, key) => {
        return changes[key] === '' ? [...scrubArr, key] : scrubArr;
      },
      [],
    );
    const scrubbedChanges =
      toScrubArray.length > 0 ? omit(toScrubArray, changes) : changes;

    const applicationUpdates = {
      ...pickedFields,
      propertyId,
      ...scrubbedChanges,
    };

    actions.updateApplication(
      applicationUpdates,
      'successDescriptionQualifcation',
      residentId,
    );
    actions.getOneResident(residentId);
  };

  resetModal = () => {
    // Also clears newMemberType so a later Add (AddHouseholdMemberModal)
    // always re-opens unselected -- AC#5, "Cancel before initial save"
    // must leave no residual state. Harmless for every other modal that
    // shares this same onClose/dismiss handler, since newMemberType is only
    // ever read while the Add-Household-Member flow is open.
    this.setState({ modal: null, newMemberType: '', moveInStay: null });
  };

  isEditHouseholdMemberDatesEnabled = () => {
    return isEditHouseholdMemberDatesFlagEnabled(this.props.flags);
  };

  editOccupant = (customerId: string) => {
    const allHouseholdMembers = [
      ...(this.props.householdMembers ?? []),
      ...(this.props.houseHoldPendingMembers ?? []),
    ];
    const selectedOccupant =
      allHouseholdMembers.find((member) => member?.id === customerId) ?? null;
    // Pending Applicants' `type` here is Redux's getHouseholdPendingMembers
    // selector's raw applicantType.name (e.g. "Head"), not 'adult'/'minor' —
    // never reliable for this check. Any Pending member is eligible
    // regardless (minors don't have a Pending state — "there is no
    // application for a Minor" — so a Pending member is always an adult).
    const isEligibleForAffordableMiniProfile =
      ['adult', 'minor', 'child'].includes(selectedOccupant?.type) ||
      selectedOccupant?.status === 'Pending';

    this.setState({
      occupantToEdit: customerId,
      modal:
        this.isEditHouseholdMemberDatesEnabled() &&
        isEligibleForAffordableMiniProfile
          ? 'editAffordableMiniProfile'
          : 'editOccupantForm',
    });
  };

  addOccupant = () => {
    // AC#6: this early-return keeps the entire new Add flow (chooser modal
    // + Add Mini-Profile) unreachable whenever edit-household-member-dates
    // is off, same as before this change.
    if (!this.isEditHouseholdMemberDatesEnabled()) {
      return;
    }

    // ENG-30200 AC#1: open the new member-type chooser first, instead of
    // jumping straight into the Mini-Profile. newMemberType is reset here
    // (in addition to resetModal) so a stale selection from a previous,
    // cancelled Add attempt can never leak into a fresh one.
    this.setState({
      occupantToEdit: null,
      newMemberType: '',
      modal: 'addHouseholdMemberType',
    });
  };

  // Continue on AddHouseholdMemberModal -- AC#1 -> AC#2: closes the chooser
  // and opens MiniProfileBridge (via newOccupantInfo below) with the
  // selected type.
  handleAddHouseholdMemberTypeContinue = (memberType: string) => {
    this.setState({
      newMemberType: memberType,
      modal: 'editAffordableMiniProfile',
    });
  };

  handleEditOccupantSubmit = (values: Object) => {
    const scrubNulls = (section: Object): ?Object =>
      section ? reject(isNil, section) : null;
    const setNulls = (section: Object, fieldNames: Array<string>) => {
      const setNull = (value) =>
        value === '' || value === 'default' ? null : value;
      const transformations = fieldNames.reduce((transformationObj, key) => {
        return {
          ...transformationObj,
          [key]: setNull,
        };
      }, {});
      return evolve(transformations, section);
    };
    const yesNoToBool = (value) => value === 'yes';
    const {
      actions: { updateResident },
      applicationInfo,
      propertyClassType,
      residentId,
    } = this.props;
    const { id, type, minorsResId } = values;
    const isAffordable = resolveIsAffordableContext({
      propertyClassType,
      applicantPropertyClass: applicationInfo?.propertyClass?.name ?? '',
      unit: this.props.unit,
    });
    const isConventional =
      propertyClassType === 'Conventional' || propertyClassType === 'Mixed';
    let affordableInformation = null;
    if (isAffordable) {
      const setAffordableNulls = setNulls(values.affordableInformation, [
        'gender',
        'handicapped',
        'race',
        'ethnicity',
        'studentType',
      ]);
      affordableInformation = scrubNulls(setAffordableNulls);
      if (affordableInformation?.multiRace) {
        const sanitizedMultiRace =
          affordableInformation.multiRace.filter(Boolean);
        affordableInformation.multiRace =
          sanitizedMultiRace.length > 0 ? sanitizedMultiRace : null;
      }
      if (affordableInformation && affordableInformation.handicapped) {
        const handicappedValue = pathOr(
          null,
          ['handicapped'],
          affordableInformation,
        );
        if (handicappedValue) {
          affordableInformation.handicapped = yesNoToBool(handicappedValue);
        }
      }
    }
    const generalInformationWithNulls = setNulls(values.generalInformation, [
      'suffixId',
      'preferredName',
      'middleName',
      'relationshipId',
    ]);
    const generalInformation =
      type === 'adult'
        ? generalInformationWithNulls
        : omit(['phoneNumber', 'emailAddress'], generalInformationWithNulls);

    const personalInformation = clearIncompleteIdentificationPairs(
      setNulls(values.personalInformation, [
        'alienRegistration',
        'passportCountry',
        'passportNumber',
        'stateIdNumber',
        'stateIdOrigin',
        'maritalStatus',
        'socialSecurityNumber',
        'dateOfBirth',
      ]),
    );
    const specialNeedsDesignationIds =
      values?.reportingInformation?.specialNeedsDesignationIds ?? [];
    const specialNeedsAffordableChange =
      this.props?.flags?.specialNeedsAffordableChange ?? false;
    const specialNeedsDesignationValues = specialNeedsAffordableChange
      ? { specialNeedsDesignationIds }
      : {};
    const annualIncome = values?.reportingInformation?.annualIncome ?? null;
    const parsedAnnualIncome = parseFloat(annualIncome);
    const annualIncomeValues =
      isConventional && specialNeedsAffordableChange
        ? {
            annualIncome:
              annualIncome && !Number.isNaN(parsedAnnualIncome)
                ? Number(parsedAnnualIncome.toFixed(2))
                : null,
          }
        : {};
    const MINCNumber = values?.reportingInformation?.MINCNumber ?? null;
    const MINCNumberValues =
      isAffordable && MINCNumber
        ? {
            MINCNumber,
          }
        : {};

    const initialResidentInfo = {
      affordableInformation,
      generalInformation,
      personalInformation,
      minorsResId,
      ...specialNeedsDesignationValues,
      ...annualIncomeValues,
      ...MINCNumberValues,
    };
    const residentUpdates = scrubNulls(initialResidentInfo);
    const residentInfo = { residentUpdates, id, residentId, type };
    updateResident(residentInfo);
    setTimeout(() => {
      queryClient.invalidateQueries([
        'getHouseholdProfileAffordableQualificationsByProgram',
      ]);
      queryClient.invalidateQueries(['fetchHouseholdHasOpenCerts']);
    }, 2000);
  };

  // The Snapshot pencil corrects the current stay.
  showMoveInDateModal = () => {
    this.setState({ modal: 'editMoveInDateModal', moveInStay: null });
  };

  showUnitHistoryModal = () => {
    this.setState({ modal: 'unitHistoryModal' });
  };

  // A Unit History row's pencil: the same modal, set to that stay.
  showStayMoveInDateModal = (stay: UnitHistoryStay) => {
    this.setState({ modal: 'editMoveInDateModal', moveInStay: stay });
  };

  showMoveOutDateModal = () => {
    this.setState({ modal: 'editMoveOutDateModal' });
  };

  showPropertyMoveInDateModal = () => {
    this.setState({ modal: 'editPropertyMoveInDateModal' });
  };

  // The user actions left after a date correction (from what really ran), listed in the success
  // toast under the message.
  renderFollowUpsToast = ({ followUps }: { followUps?: Array<string> }) =>
    followUps?.length ? (
      <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
        {followUps.map((followUp) => (
          <li key={followUp}>{followUp}</li>
        ))}
      </ul>
    ) : undefined;

  handleMoveInDateModalSubmit = (
    values: Object,
    options?: { correctedLease?: ?File },
  ) => {
    const {
      residentInfo: { id },
    } = this.props;
    const { updateMoveInDate } = this.props.actions;

    // With household-mi-mo-date-changes on, the modal stays open while saving.
    // The saga reports the outcome through onSuccess/onError, and the returned
    // promise lets redux-form show the server message under the date field
    // (SubmissionError) instead of a toast.
    if (isHouseholdMiMoDateChangesFlagEnabled(this.props.flags)) {
      // The Corrected Lease the user chose in the modal (ENG-33327), stored with the correction.
      const correctedLease = options?.correctedLease ?? null;
      return new Promise((resolve, reject) => {
        updateMoveInDate({
          residentId: id,
          modify: {
            actualMoveInDate: formatDateLocale(values.editMoveDate),
            // The stay opened from the Unit History (its move-in lease); the server corrects the
            // current stay without it.
            ...(this.state.moveInStay
              ? { stayLeaseId: this.state.moveInStay.stayLeaseId }
              : {}),
          },
          ...(correctedLease ? { correctedLease } : {}),
          success: 'Move-In Date Updated',
          renderToastComponent: this.renderFollowUpsToast,
          onSuccess: () => {
            this.resetModal();
            this.refreshActivityTable();
            queryClient.invalidateQueries(['householdOccupancyPeriods']);
            queryClient.invalidateQueries([UNIT_HISTORY_QUERY_KEY]);
            // The save can move members' Start Dates (those equal to the old Move-In): reload the
            // Household table (Redux) and the Mini-Profile ('getResident').
            this.props.actions.getResidentHousehold(id);
            queryClient.invalidateQueries(['getResident']);
            // The Corrected Lease was stored with the save: reload the Documents tab.
            if (correctedLease) {
              this.props.actions.setDocumentsListStatus('changed');
            }
            resolve();
          },
          onError: (message: string) => {
            reject(new SubmissionError({ editMoveDate: message }));
          },
        });
      });
    }

    const leaseChangeset = {
      actualMoveInDate: values.editMoveDate,
    };

    this.resetModal();
    updateMoveInDate({
      residentId: id,
      modify: leaseChangeset,
      success: 'Move-In Date Updated',
      refreshActivityTable: this.refreshActivityTable,
    });
  };

  handleMoveOutDateModalSubmit = (values: Object) => {
    const {
      residentInfo: { id },
    } = this.props;
    const { updatePriorResidentMoveOutDate } = this.props.actions;

    // With household-mi-mo-date-changes on, the prior resident's final stay is corrected
    // (ENG-32980) as the Move-In is: the modal stays open while saving, and the returned promise
    // lets redux-form show the server message under the date field (SubmissionError).
    if (isHouseholdMiMoDateChangesFlagEnabled(this.props.flags)) {
      return new Promise((resolve, reject) => {
        updatePriorResidentMoveOutDate({
          residentId: id,
          modify: { moveOutDate: formatDateLocale(values.editMoveDate) },
          success: 'Move-Out Date Updated',
          renderToastComponent: this.renderFollowUpsToast,
          onSuccess: () => {
            this.resetModal();
            this.refreshActivityTable();
            queryClient.invalidateQueries(['householdOccupancyPeriods']);
            queryClient.invalidateQueries([UNIT_HISTORY_QUERY_KEY]);
            // The save moves members' End Dates (those equal to the old Move-Out): reload the
            // Household table, which reads Redux, not React Query.
            this.props.actions.getResidentHousehold(id);
            // The Mini-Profile reads the Move-Out Date from 'getResident', and the open-cert
            // guards read 'hasOpenCerts': a HUD correction or reopen started by the save opens a cert.
            queryClient.invalidateQueries(['getResident']);
            queryClient.invalidateQueries(['hasOpenCerts']);
            resolve();
          },
          onError: (message: string) => {
            reject(new SubmissionError({ editMoveDate: message }));
          },
        });
      });
    }

    const leaseChangeset = {
      moveOutDate: values.editMoveDate,
    };

    this.resetModal();
    updatePriorResidentMoveOutDate({
      residentId: id,
      modify: leaseChangeset,
      success: 'Move-Out Date Updated',
      refreshFn: () => {
        queryClient.invalidateQueries([
          'getHouseholdProfileAffordableQualificationsByProgram',
        ]);
      },
    });
  };

  handlePropertyMoveInDateModalSubmit = (values: Object) => {
    const {
      residentInfo: { id },
    } = this.props;
    const { updatePropertyMoveInDate } = this.props.actions;

    // With household-mi-mo-date-changes on, the modal stays open while saving.
    // The saga reports the outcome through onSuccess/onError, and the returned
    // promise lets redux-form show server validation/permission errors under
    // the date field (SubmissionError) instead of a toast.
    if (isHouseholdMiMoDateChangesFlagEnabled(this.props.flags)) {
      return new Promise((resolve, reject) => {
        updatePropertyMoveInDate({
          residentId: id,
          modify: {
            householdMoveInDate: formatDateLocale(
              values.editPropertyMoveInDate,
            ),
          },
          success: 'Property Move-In Date Updated',
          onSuccess: () => {
            this.resetModal();
            this.refreshActivityTable();
            resolve();
          },
          onError: (message: string) => {
            reject(new SubmissionError({ editPropertyMoveInDate: message }));
          },
        });
      });
    }

    const ModifiedPropertyMoveInDate = {
      householdMoveInDate: values.editPropertyMoveInDate,
    };

    this.resetModal();
    updatePropertyMoveInDate({
      residentId: id,
      modify: ModifiedPropertyMoveInDate,
      success: 'Property Move-In Date Updated',
    });
  };
  sendPayLeaseEmail = (customerId: string) => {
    this.props.actions.sendResidentPayLeaseEmail(customerId);
  };

  getMonthsSinceMoveIn = (currentMoveInDate: any) => {
    const now = moment();
    const momentCurrentMoveInDate = moment(currentMoveInDate);
    return now.diff(momentCurrentMoveInDate, 'months');
  };

  handleMoveOutDateUpdate = (date: any) => {
    this.setState({
      actualMoveOutDate: moment(
        moment.utc(date).local().format('MM/DD/YYYY'),
        'MM/DD/YYYY',
      ),
    });
  };

  overrideDisabledRecertification = () => {
    this.setState({ recertEnabled: !this.state.recertEnabled });
  };

  setMissingInitialCert = (numberOfCerts: number) => {
    const { residentInfo } = this.props;
    const residentWithNoCerts = numberOfCerts === 0;
    const actualMoveInDate = pathOr(
      null,
      ['lease', 'actualMoveInDate'],
      residentInfo,
    );
    const firstYearResident = actualMoveInDate
      ? moment(actualMoveInDate).isAfter(moment().subtract(1, 'years'))
      : false;
    const missingInitialCert = residentWithNoCerts && firstYearResident;
    if (missingInitialCert) {
      this.setState({ missingInitialCert: true });
    }
  };

  selectCorrectionTab = (programName?: string) => {
    if (programName) {
      this.syncResidentProfileTabToUrl(programName);
      this.setState({ ...this.state, key: programName });
    }
  };

  selectTab = (tabEventKey: string) => {
    this.syncResidentProfileTabToUrl(tabEventKey);
    this.setState({ ...this.state, key: tabEventKey });
  };

  syncResidentProfileTabToUrl = (nextTabKey: string) => {
    const { history, location } = this.props;
    const existing = parse(location.search.replace('?', '') || '');
    const nextQuery = { ...existing, tab: nextTabKey };
    history.replace({
      pathname: location.pathname,
      search: `?${stringify(nextQuery)}`,
    });
  };

  handleResidentProfileTabChange = (key: string) => {
    this.syncResidentProfileTabToUrl(key);
    this.setState({ ...this.state, key });
  };

  getResidentButtonsMessage = (type: 'transfer' | 'renewal') => {
    const { residentInfo, unit, applicationInfo } = this.props;

    const isCommercial = applicationInfo.isCommercial === true;

    const {
      household: { doNotRenew, underEviction },
    } = residentInfo;
    // $FlowFixMe
    const unitStatus = pathOr(false, ['unitStatus', 'description'], unit);

    if (underEviction) return '"Under Eviction" is selected.';
    if (doNotRenew) return '"Do Not Renew" is selected.';
    if (
      !isCommercial &&
      type === 'transfer' &&
      unitStatus !== 'Occupied / On Notice to Vacate'
    )
      return 'Must enter NTV to begin transfer.';
  };

  refreshActivityTable = () => {
    this.setState({
      activityTableUpdateTrigger: !this.state.activityTableUpdateTrigger,
    });
    queryClient.invalidateQueries([
      'getHouseholdProfileAffordableQualificationsByProgram',
    ]);
    queryClient.invalidateQueries([
      'getCompletionStatusesAndAssistanceDetails',
    ]);
    queryClient.invalidateQueries([
      'getAffordableQualificationCompletionStatuses',
    ]);
  };

  // At the moment of triggering the above from the modals it doesn't actually refreshes the table properly
  forceRefreshActivityTable = () => {
    this.setState(
      {
        activityTableUpdateTrigger: !this.state.activityTableUpdateTrigger,
      },
      () => this.refreshActivities(),
    );
  };

  setHasHouseholdRentableItems = (hasHouseholdRentableItems: boolean) => {
    this.setState({ hasHouseholdRentableItems });
  };

  setHasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate = (
    hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate: boolean,
  ) => {
    this.setState({
      hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate,
    });
  };

  render() {
    if (!this.props.prospectInfo.id) {
      return <AsyncBox loading={true} sx={{ height: 'calc(100vh - 40px)' }} />;
    }

    const onChecklistOptionChangeCurried = curryN(
      4,
      this.handleChecklistOptionChange.bind(this),
    );
    const {
      affordableRelationships,
      applicantCancellationReasons,
      applicants,
      applicationDecisionStatus,
      applicationInfo,
      applicationStatuses,
      assignedTo,
      basicLeaseFees,
      countries,
      currentLease,
      currentUser,
      deletedApplicants,
      financiallyResponsibleHousehold,
      hasOpenTransfer,
      hasOpenRenewal,
      history,
      householdMembers,
      houseHoldPendingMembers,
      intl,
      isFutureLease,
      isResident,
      isPriorResident,
      labelEndDate,
      lateMethods,
      leases,
      ledgerBalance,
      monthsWithLateFees,
      monthsWithNSFFees,
      openFiscalPeriod,
      isFirstFiscalPeriod,
      propertyClassType,
      prospectInfo,
      residentId,
      residentInfo,
      residents,
      screening,
      securityDeposits,
      selectedMonthlyOption,
      selectedProperty,
      states,
      submittingRenewal,
      suffixes,
      transferInformation,
      unit,
      users,
      leaseSignatureStatuses,
      relationshipOptions,
      minorRelationshipOptions,
      applicantTypeOptions,
      agedUpMinorApplicantTypeId,
      affordableQualifications,
      specialNeedsDesignations,
      recertDueDates,
      firstUnitMoveInAfterPriorResident,
      moveOutProratedRentMethodId,
      deprecated_moveOutProratedRentMethodId,
      prorateMethods,
      moveOutProrateMethods,
      flags,
      guarantorOptions,
      liveInCaretakerOptions,
      locale,
    } = this.props;

    const isRDByUnit = (
      unit?.floorPlan?.floorPlanAffordablePrograms ?? []
    ).some(
      (fp) =>
        fp?.propertyAffordableProgram?.masterAffordableProgram?.name ===
        RD_PROGRAM_NAME,
    );
    const hasAssignedUnit = unit?.id?.length;
    const roundProratedRents =
      selectedProperty?.config?.roundProratedRents || false;
    const proRatedTransactions = getMoveOutProratedTransactions(
      this.state.monthlyTransactionsForProrateCalculator,
      this.state.actualMoveOutDate,
      this.props.moveInApplyFullAmountToProRate,
      roundProratedRents,
      isRDByUnit,
    );

    const { hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate } =
      this.state;

    const {
      lease,
      lease: { fasReady, anticipatedMoveOutDate },
      household: {
        doNotRenew,
        underEviction,
        paymentsDoNotAccept,
        paymentsCertifiedOnly,
        householdMoveInDate,
        autoEmailMonthlyInvoices,
        doNotAcceptPartialPayments,
        mainCustomerId,
      },
      householdId,
      residentCustomer: {
        customerId,
        customer: { firstName, lastName },
      },
    } = residentInfo;

    const transferDate =
      lease?.isTransfer || lease?.isTransferDone ? lease?.startDate : null;
    const { moveOutDate } = lease?.noticeToVacate || {};
    const actualOrAnticipatedMoveOutDate =
      moveOutDate || anticipatedMoveOutDate;

    // $FlowFixMe
    const householdInfo = pathOr({}, ['household'], residentInfo);
    const getApprovedStatusId = (applicationStatuses: Array<Object>) => {
      const approvedStatus = applicationStatuses.find(
        (status) => status.text === 'Approved',
      );
      return pathOr(null, ['value'], approvedStatus);
    };
    const pendingApplicants = pathOr([], ['pendingApplicants'], applicants);
    // FES-1221: set only when every pending application is an aged-up-minor conversion, and
    // then the earliest of their majority birthdays. Certifications effective before it still
    // count those members as minors, so they must not be blocked.
    const agedUpOnlyPendingMajorityDate =
      applicants?.agedUpOnlyPendingMajorityDate ?? null;

    const status = isResident ? 'Resident' : 'Prior Resident';
    const isCommercial = applicationInfo.isCommercial === true;
    const currentMoveInDate = currentLease.moveInDate;
    const currentMoveOutDate = currentLease.moveOutDate;

    // $FlowFixMe
    const unitStatus = pathOr(false, ['unitStatus', 'description'], unit);
    const isOnNTV = unitStatus === 'Occupied / On Notice to Vacate';
    const isDisabledRenewal = ifElse(
      always(
        any(equals(true))([
          doNotRenew,
          underEviction,
          hasOpenRenewal,
          isFutureLease,
          isNil(unit),
          isEmpty(unit),
        ]),
      ),
      always(true),
      always(false),
    )();
    const isDisabledTransfer = any(equals(true))([
      !isOnNTV,
      doNotRenew,
      underEviction,
      hasOpenTransfer,
      hasOpenRenewal,
      isFutureLease,
      isNil(unit),
      isEmpty(unit),
    ]);
    const startTransferMessage = !isCommercial
      ? this.getResidentButtonsMessage('transfer')
      : undefined;
    const startRenewalMessage = this.getResidentButtonsMessage('renewal');
    const newestLease = leases.length > 0 ? leases[0] : {};
    const parsedNewestLease = getSelectedLease(
      leases,
      newestLease.id,
      this.props.flags,
    );

    /**
     * When some events happen on the CAM tab (such as confirm estimates, reconcile, cancel CAM, ...),
     * the ledger should be refreshed.
     */
    const onCamRefresh = () => {
      // Refresh CAM Pools
      const householdId = pathOr(
        null,
        ['residentInfo', 'householdId'],
        this.props,
      );
      if (householdId) {
        this.props.actions.fetchCamPools(householdId);
      }

      // refresh ledger
      this.props.actions.refreshLedger(customerId, true);
    };

    // $FlowFixMe
    const leaseRentPercentage = pathOr(
      '20',
      ['applicationInfo', 'property', 'leaseRentPercentage'],
      this.props,
    );

    const isAffordable = resolveIsAffordableContext({
      propertyClassType,
      applicantPropertyClass: applicationInfo?.propertyClass?.name ?? '',
      unit,
    });
    // ENG-34106: the qualification tabs only. isAffordable keeps driving everything else (occupant
    // requirements, the resident-update payload, addenda) unchanged.
    const showQualificationTabs = resolveShowQualificationTabs({
      isAffordable,
      affordableQualifications,
      householdPrograms: this.props.propertyAffordablePrograms,
      transferTargetPrograms: this.props.transferTargetPrograms,
    });
    const showDocumentsTab = this.checkUserPermissions(['document-read']);
    const showTextingTab =
      selectedProperty.isTwoWayCommunicationActive &&
      this.checkUserPermissions(['communication-create']);

    const actualMoveInDate = pathOr(null, ['actualMoveInDate'], lease);
    const currentFiscalStartDate = pathOr(
      null,
      ['startDate'],
      openFiscalPeriod,
    );

    const isMiMoDateChangesEnabled = isHouseholdMiMoDateChangesFlagEnabled(
      this.props.flags,
    );

    // Flag off: today's visibility — a current resident whose Move-In is
    // before the open fiscal period.
    let showEditMoveInDateLinkFlag = false;
    if (
      !isMiMoDateChangesEnabled &&
      actualMoveInDate &&
      currentFiscalStartDate
    ) {
      const momentMoveInDate = moment(actualMoveInDate);
      const momentFiscalStartDate = moment(currentFiscalStartDate);

      if (
        isResident &&
        momentMoveInDate.isBefore(momentFiscalStartDate, 'days')
      ) {
        showEditMoveInDateLinkFlag = true;
      }
    }

    // Flag on (§3.2): the pencil shows for current and prior residents whatever
    // the fiscal period (a date inside it is rejected in the modal). It is
    // disabled, with a specific tooltip, when the user lacks the Move-In edit
    // permission (ENG-33670), or while the household has an open transfer, an
    // open lease renewal or an active move-out. A stay created by a completed
    // transfer is corrected here too: the origin unit's Move-Out
    // moves with it. The affordable-unit disable is removed. The pencil always
    // corrects the current stay; earlier stays are corrected from the Unit
    // History.
    // The permission is checked here rather than through ElementWithPermissions:
    // that wrapper disables its direct child, which for the Move-In pencil is the
    // Tooltip, so the link stayed clickable.
    const canEditMoveInDates = this.checkUserPermissions([
      'lease-current-lease-end-date-edit',
    ]);
    const moveInBlockerTooltip = (() => {
      if (!canEditMoveInDates) {
        return this.props.intl.formatMessage(messages.moveInDateNoPermission);
      }
      if (
        leases.some((l) => l?.isTransfer === true && l?.isTransferDone !== true)
      ) {
        return this.props.intl.formatMessage(
          messages.moveInDateBlockedOpenTransfer,
        );
      }
      // An open renewal is a renewal of the household's active lease only
      // (priorLeaseId === activeLeaseId); stale renewals of older leases and
      // prior residents (no active lease) don't block, matching the backend.
      const activeLeaseId = householdInfo?.activeLeaseId ?? null;
      if (
        activeLeaseId != null &&
        leases.some(
          (l) =>
            l?.priorLeaseId === activeLeaseId &&
            l?.isRenewal === true &&
            l?.isRenewalComplete !== true,
        )
      ) {
        return this.props.intl.formatMessage(
          messages.moveInDateBlockedOpenRenewal,
        );
      }
      if (isResident && Boolean(lease?.noticeToVacate)) {
        return this.props.intl.formatMessage(
          messages.moveInDateBlockedActiveMoveOut,
        );
      }
      return '';
    })();

    // "View Household History (N)" under the Property Move-In Date when the
    // household lived in several units here (one stay per unit and Move-In:
    // renewals share their stay's). Each row's pencil corrects that stay; the
    // stays endpoint needs the Move-In edit permission and names each stay's
    // blocker.
    const stayCount = new Set(
      leases
        .filter((l) => Boolean(l?.actualMoveInDate))
        .map((l) => `${l.unitId}|${String(l.actualMoveInDate).slice(0, 10)}`),
    ).size;
    const unitHistoryLink =
      isMiMoDateChangesEnabled && canEditMoveInDates && stayCount > 1 ? (
        <button
          type="button"
          className="btn btn-link"
          style={LINK_BUTTON_STYLE}
          onClick={this.showUnitHistoryModal}
          data-test="view-unit-history"
        >
          {this.props.intl.formatMessage(unitHistoryMessages.viewUnitHistory, {
            count: stayCount,
          })}
        </button>
      ) : null;

    const showEditMoveInDateLink = () => {
      if (isMiMoDateChangesEnabled) {
        if (!actualMoveInDate) {
          return null;
        }
        const isBlocked = Boolean(moveInBlockerTooltip);
        return (
          <Tooltip
            title={moveInBlockerTooltip}
            containerSX={{ display: 'inline' }}
          >
            <a
              onClick={() => {
                if (!isBlocked) {
                  this.showMoveInDateModal();
                }
              }}
              disabled={isBlocked}
              data-test="edit-move-in-date"
            >
              <i className="icon et-pencil" />
            </a>
          </Tooltip>
        );
      }
      if (showEditMoveInDateLinkFlag) {
        const isAffordableUnit = unit?.floorPlan?.isAffordable ?? false;
        const tooltipMessage = isAffordableUnit
          ? this.props.intl.formatMessage(
              messages.unableUpdateMoveInDateAffordable,
            )
          : '';
        return (
          <ElementWithPermissions scope={['lease-current-lease-end-date-edit']}>
            <Tooltip title={tooltipMessage} containerSX={{ display: 'inline' }}>
              <a
                onClick={() => {
                  this.showMoveInDateModal();
                }}
                disabled={isAffordableUnit}
              >
                <i className="icon et-pencil" />
              </a>
            </Tooltip>
          </ElementWithPermissions>
        );
      } else {
        return null;
      }
    };

    const showEditMoveOutDateLink = () => {
      // Flag on (ENG-32980 §3.2): the prior resident's pencil opens the Move-Out correction, and
      // is hidden without the Move-Out edit permission. Checked here rather than through
      // ElementWithPermissions, which only marks its <a> child disabled and leaves it clickable.
      // Date and stay failures come from the preview, under the field.
      if (isMiMoDateChangesEnabled) {
        if (
          isResident ||
          !this.checkUserPermissions(['prior-resident-move-out-date-edit'])
        ) {
          return null;
        }
        return (
          <button
            type="button"
            className="btn btn-link"
            style={LINK_BUTTON_STYLE}
            onClick={this.showMoveOutDateModal}
            aria-label={this.props.intl.formatMessage(moveOutMessages.title)}
            data-test="edit-move-out-date"
          >
            <i className="icon et-pencil" />
          </button>
        );
      }
      if (!isResident) {
        return (
          <ElementWithPermissions scope={['prior-resident-move-out-date-edit']}>
            <a
              onClick={() => {
                this.showMoveOutDateModal();
              }}
            >
              <i className="icon et-pencil" />
            </a>
          </ElementWithPermissions>
        );
      } else {
        return null;
      }
    };

    const isHouseholdMiMoDateChangesEnabled =
      isHouseholdMiMoDateChangesFlagEnabled(this.props.flags);
    // With household-mi-mo-date-changes on, the pencil shows for current and
    // prior residents whatever the fiscal period; a date inside the open
    // fiscal period is rejected as an error inside the modal instead.
    let showEditPropertyMoveInDateFlag =
      isHouseholdMiMoDateChangesEnabled && Boolean(householdMoveInDate);
    if (
      !isHouseholdMiMoDateChangesEnabled &&
      householdMoveInDate &&
      currentFiscalStartDate
    ) {
      const momenthouseholdMoveInDate = moment(householdMoveInDate);
      const momentFiscalStartDate = moment(currentFiscalStartDate);

      if (
        isResident &&
        momenthouseholdMoveInDate.isBefore(momentFiscalStartDate, 'days')
      ) {
        showEditPropertyMoveInDateFlag = true;
      }
    }
    const showEditPropertyMoveInDateLink = () => {
      if (showEditPropertyMoveInDateFlag && isHouseholdMiMoDateChangesEnabled) {
        // Same permission and behavior as the Move-In pencil (ENG-33670).
        return (
          <Tooltip
            title={
              canEditMoveInDates
                ? ''
                : this.props.intl.formatMessage(
                    messages.propertyMoveInDateNoPermission,
                  )
            }
            containerSX={{ display: 'inline' }}
          >
            <a
              onClick={() => {
                if (canEditMoveInDates) {
                  this.showPropertyMoveInDateModal();
                }
              }}
              disabled={!canEditMoveInDates}
              data-test="edit-property-move-in-date"
            >
              <i className="icon et-pencil" />
            </a>
          </Tooltip>
        );
      }
      if (showEditPropertyMoveInDateFlag) {
        return (
          <ElementWithPermissions scope={['lease-current-lease-end-date-edit']}>
            <a
              onClick={() => {
                this.showPropertyMoveInDateModal();
              }}
            >
              <i className="icon et-pencil" />
            </a>
          </ElementWithPermissions>
        );
      } else {
        return null;
      }
    };

    // The Unit History's stay, else the current one (the Snapshot pencil).
    const { moveInStay } = this.state;
    const editMoveInDateModalInitial = {
      editMoveDate: moveInStay
        ? moment(moveInStay.moveInDate)
        : actualMoveInDate
        ? moment(actualMoveInDate)
        : null,
    };
    const editMoveOutDateModalInitial = {
      editMoveDate:
        !isResident && currentLease && currentLease.moveOutDate
          ? moment(currentLease.moveOutDate)
          : null,
    };
    const editPropertyMoveInDateModalInitial = {
      editPropertyMoveInDate: householdMoveInDate
        ? moment(householdMoveInDate)
        : null,
    };

    const allHouseholdMembers = householdMembers.concat(
      houseHoldPendingMembers,
    );

    const occupantToEdit =
      allHouseholdMembers.find(
        // $FlowFixMe
        (e) => pathOr('', ['id'], e) === this.state.occupantToEdit,
      ) || {};
    // ENG-30200 AC#1/AC#2: the Add flow now always goes through
    // AddHouseholdMemberModal first, which sets newMemberType before ever
    // opening 'editAffordableMiniProfile' (see
    // handleAddHouseholdMemberTypeContinue) -- the 'adult' fallback is only
    // a defensive default and should not be reachable in practice.
    const newOccupantInfo = { type: this.state.newMemberType || 'adult' };
    const frNames = financiallyResponsibleHousehold.map((f) => f.completeName);

    const paymentRestrictions = {
      doNotAccept: paymentsDoNotAccept,
      certifiedOnly: paymentsCertifiedOnly,
      doNotAcceptPartialPayments: doNotAcceptPartialPayments,
    };

    // Live-In Caretakers are non financial responsible adults
    // but they shouldn't be included in the household members list for the lease
    const nonFRAdults = householdMembers.filter(
      // $FlowFixMe
      ({ type, financiallyResponsible, status, applicantTypeId }) =>
        type === 'adult' &&
        !financiallyResponsible &&
        applicantTypeId !== liveInCaretakerOptions?.applicantType?.id &&
        status !== 'Prior',
    );

    const hasReOpenedRDCertifications = affordableQualifications?.some(
      (aq) =>
        aq?.propertyAffordableProgram?.masterAffordableProgram?.name ===
          RD_PROGRAM_NAME &&
        aq.isActive &&
        aq.certificationStatus === AFFORDABLE_QUALIFICATION_STATUSES.REOPEN,
    );

    const isHUDByUnit = (
      unit?.floorPlan?.floorPlanAffordablePrograms ?? []
    ).some(
      (fp) =>
        fp?.propertyAffordableProgram?.masterAffordableProgram?.name ===
        HUD_PROGRAM_NAME,
    );

    const editMoveInValidateFiscalPeriod = (moveInDate) => {
      const fiscalPeriodStartDate = pathOr(
        null,
        ['startDate'],
        openFiscalPeriod,
      );
      if (moveInDate && fiscalPeriodStartDate) {
        // The period start is a UTC instant; the picked date is a local calendar day.
        const startDay = moment.utc(fiscalPeriodStartDate).format('YYYY-MM-DD');
        const moveInDay = moment(moveInDate).format('YYYY-MM-DD');

        if (moveInDay >= startDay) {
          const formattedStartDate = moment
            .utc(fiscalPeriodStartDate)
            .format('MM-DD-YYYY');
          return `Move-In Date cannot be a date in the current fiscal period (on or after ${formattedStartDate}).`;
        }
      }
      return null;
    };

    const ntv = pathOr(null, ['lease', 'noticeToVacate'], unit);

    const editMoveOutValidation = (newMoveOutDate) => {
      if (!newMoveOutDate) {
        return;
      }

      const momentNewMoveOutDate = moment(newMoveOutDate);

      // Move-out cannot be in the future from now
      if (moment().isBefore(momentNewMoveOutDate, 'days')) {
        return 'Move-Out Date cannot be in the future';
      }

      // Move-out cannot be before move-in
      if (actualMoveInDate) {
        const momentActualMoveInDate = moment(actualMoveInDate);
        if (momentActualMoveInDate.isAfter(momentNewMoveOutDate, 'days')) {
          const formatted = momentActualMoveInDate.format('MM-DD-YYYY');
          return `Move-Out Date cannot be before the Move-In date (on or after ${formatted})`;
        }
      }

      // Move-out cannot be later than the current one if another resident has moved in
      // the same unit
      if (firstUnitMoveInAfterPriorResident) {
        const momentFirstMoveIn = moment(firstUnitMoveInAfterPriorResident);
        if (momentFirstMoveIn.isBefore(momentNewMoveOutDate, 'days')) {
          const formatted = momentFirstMoveIn.format('MM-DD-YYYY');
          return `Move-Out Date cannot be after another resident moved in (on or before ${formatted})`;
        }
      }
    };

    const isCAMActive = pathOr(
      false,
      ['applicationInfo', 'property', 'isCAMActive'],
      this.props,
    );

    // show CAM Tab both for prior and current tenants
    // only for commercial units on properties with active CAM
    const showCamTab = isCommercial && isCAMActive;
    const showCamPoolsOnCreateTransaction =
      showCamTab &&
      !isNil(this.props.camPools) &&
      !isEmpty(this.props.camPools);
    const camPoolsForTransactions = showCamPoolsOnCreateTransaction
      ? this.props.camPools
      : null;

    const nonOptionalCharge = getNonOptionalChargeFromQualifications(
      affordableQualifications,
    );

    const miniProfileApplicant =
      applicationInfo?.applicants?.find((applicant) => {
        return applicant?.resident?.id === this.state.occupantToEdit;
      }) ?? null;

    // Check applicant annual income which comes from saving from mini-profile
    // if doesn't exist populate with applicantCustomer data which comes from application forms
    const initialAnnualIncome = miniProfileApplicant?.annualIncome
      ? miniProfileApplicant.annualIncome
      : miniProfileApplicant?.applicantCustomer?.annualIncome ?? '0.00';
    const initialMINCNumber = miniProfileApplicant?.applicantMinor
      ? miniProfileApplicant?.applicantMinor?.MINCNumber
      : miniProfileApplicant?.applicantCustomer?.customer?.MINCNumber ?? null;
    const reportingInformationInitialValues = {
      specialNeedsDesignationIds:
        miniProfileApplicant?.specialNeedsDesignationIds ?? [],
      annualIncome: initialAnnualIncome,
      MINCNumber: initialMINCNumber,
    };

    const appliesForPhaseIn =
      householdInfo?.householdPhaseInEligibility?.appliesForPhaseIn;

    const onCreateFinalAccountStatementReversal = (
      finalAccountStatementReversal,
    ) => {
      this.props.actions.getOneResident(residentId);
      this.props.actions.refreshLedger(customerId, true);
      this.refreshActivityTable();
    };

    const { programs, affordableTabs, hudTab, householdUtilityAllowanceId } =
      configureResidentApplicantAffordableTabsDisplay(
        affordableQualifications,
        selectedProperty,
        unit || {},
        flags,
        showQualificationTabs,
        false,
        this.props.propertyAffordablePrograms,
        // ENG-29435: mid-transfer, union in the target floor plan's programs. Empty unless a
        // transfer is IN_PROGRESS and the unitTransfers flag is on.
        this.props.transferTargetPrograms,
      );

    const isReceivingAssistance = getIsReceivingAssistance(
      affordableTabs || [],
    );

    const showHUD =
      isResident || isPriorResident
        ? Boolean(hudTab)
        : Boolean(hudTab && hudTab.affordableQualificationId);

    const section236Flag = flags?.section236;
    const isSection236 = checkIsSection236({
      shouldCheck: showHUD && section236Flag,
      section236Flag,
      unit,
      selectedProperty,
    });

    const hasMoveOutProratedTransactions =
      this.props?.moveOutProratedTransactions?.length > 0;
    const moveOutProratedTransactionsDate =
      this.props?.moveOutProratedTransactions?.[0]?.lmt?.col?.[0]?.createdAt ??
      null;

    const showAffordableTabs =
      showQualificationTabs && affordableTabs?.length > 0;

    const selectedTabKey = resolveSelectedResidentTab(
      this.state.key,
      (affordableTabs ?? []).map((tab) => tab.urlProgramName),
    );

    if (this.state.processMoveOut) {
      const moveOutDate = this.state.actualMoveOutDate
        ? moment(
            moment
              .utc(this.state.actualMoveOutDate)
              .local()
              .format('MM/DD/YYYY'),
            'MM/DD/YYYY',
          )
        : null;
      const moveOutProratedRentAdminSettingFlag =
        flags?.moveOutProratedRentAdminSetting;
      const moveOutProratedRentMethod = moveOutProratedRentAdminSettingFlag
        ? (prorateMethods.find(
            (prm) => prm.id === deprecated_moveOutProratedRentMethodId,
          )?.name ?? PRORATE_METHODS.NO_NEVER) === PRORATE_METHODS.YES_ALWAYS
        : true;

      const getShouldProrate = (methodId) => {
        const method =
          moveOutProrateMethods.find((method) => {
            return method.id === methodId;
          }) ?? MOVE_OUT_PRORATE_MEHTODS.NO_NEVER;

        return [
          MOVE_OUT_PRORATE_MEHTODS.YES_FULL_MONTH,
          MOVE_OUT_PRORATE_MEHTODS.YES_MONTH_START,
        ].includes(method?.name);
      };
      const shouldProrateAtMoveOut = flags?.prorateRentsForMoveOuts
        ? getShouldProrate(moveOutProratedRentMethodId)
        : moveOutProratedRentMethod;

      const proRatedTransactionsToProcess = shouldProrateAtMoveOut
        ? proRatedTransactions
        : [];

      return (
        <ResidentMoveOut
          cancel={this.onCancelProcessMoveOutClick}
          intl={intl}
          initialValues={{
            moveOutDate,
            proRatedTransactions: proRatedTransactionsToProcess,
          }}
          customerName={`${firstName} ${lastName}`}
          unitNumber={unit ? unit.number : ''}
          moveOutClick={this.confirmMoveOutModal}
          onSubmit={this.handleSubmitMoveOut}
          openFiscalPeriod={this.props.openFiscalPeriod}
          isFirstFiscalPeriod={isFirstFiscalPeriod}
          moveOutDateStatus={this.moveOutDateStatus}
          monthlyTransactions={proRatedTransactionsToProcess}
          handleMoveOutDateUpdate={this.handleMoveOutDateUpdate}
          moveInApplyFullAmountToProRate={
            this.props.moveInApplyFullAmountToProRate
          }
          moveOutProratedRent={shouldProrateAtMoveOut}
          roundProratedRents={roundProratedRents}
          residentId={residentId}
          isMoveOutRequestProcessing={this.props.isMoveOutRequestProcessing}
          backdateMoveOutDateMoreThan30DaysFlag={
            this.props.flags?.backdateMoveOutDateMoreThan30Days
          }
          isAffordable={unit?.floorPlan?.isAffordable ?? false}
          hasGRCerts={isRDByUnit || isHUDByUnit}
          organizationId={selectedProperty?.organizationId}
          propertyId={selectedProperty?.id}
          householdId={householdId}
          isHUDByUnit={isHUDByUnit}
          isSection236={isSection236}
          floorPlanAffordablePrograms={
            unit?.floorPlan?.floorPlanAffordablePrograms ?? []
          }
          selectedProperty={selectedProperty}
        />
      );
    }

    if (this.state.processTransfer) {
      const newUnit = pathOr([{}], ['units'], parsedNewestLease)[0];
      return (
        <TransferScreenContainer
          handleCancel={this.handleTransferCancel}
          intl={intl}
          lease={currentLease}
          customerName={`${firstName} ${lastName}`}
          userId={currentUser.user.id}
          leaseId={currentLease.id}
          unitNumber={newUnit ? newUnit.number : ''}
          openFiscalPeriod={this.props.openFiscalPeriod}
          residentId={residentId}
        />
      );
    }

    return (
      <LeaseDataTabProvider leaseId={parsedNewestLease.id}>
        <DocumentTitle
          title={this.props.intl.formatMessage(messages.title)}
          className="creditstatus-page"
        >
          <NoPrint>
            <HouseholdNotifications
              organizationId={selectedProperty.organizationId}
              propertyId={selectedProperty.id}
              householdId={householdId}
            />
            <ProfileLayout
              ContactBox={
                <ResidentProfileDetails
                  applicants={applicationInfo.applicants}
                  isResident={isResident}
                  unitNumber={unit ? unit.number : ''}
                  unitId={unit ? unit.id : ''}
                  isCommercial={isCommercial}
                  isUnitSoftDeleted={typeof unit?.deletedAt === 'string'}
                  floorPlanAtMoveOut={this.props.floorPlanAtMoveOut ?? ''}
                  showFloorPlanAtMoveOut={Boolean(
                    this.props.flags?.floorPlanChanges,
                  )}
                  floorPlanId={unit?.floorPlan?.id}
                  floorPlanName={unit?.floorPlan?.internalName}
                  showCurrentFloorPlan={Boolean(
                    this.props.flags?.floorPlanChanges,
                  )}
                  residentId={residentId}
                  organizationId={selectedProperty.organizationId}
                  propertyId={selectedProperty.id}
                  householdId={householdId}
                />
              }
              ResidentTransferCard={
                this.props.flags?.unitTransfers ? (
                  <ResidentTransferCard
                    organizationId={selectedProperty.organizationId}
                    propertyId={selectedProperty.id}
                    householdId={householdId}
                    enabled={Boolean(this.props.flags?.unitTransfers)}
                    transferReasonOptions={
                      this.props.transferMoveOutReasonOptions ?? []
                    }
                    onTransferChanged={() => {
                      const { residentId } = this.props;
                      // A transfer-date change resets the transfer lease server-side. Only
                      // getOneResident (GET_ONE_RESIDENT_SUCCESS) repopulates
                      // residentProfile.leases, which is what the Lease Data tab reads — so this
                      // must use it (not getResidentHousehold, which writes a different slice and
                      // would leave the tab showing stale transfer-lease dates). (ENG-30152)
                      if (isValidResidentId(residentId)) {
                        this.props.actions.getOneResident(residentId);
                      }
                    }}
                    onCancelSuccess={() => {
                      const { residentId } = this.props;
                      // getOneResident is the only action that repopulates
                      // residentProfile.leases (GET_ONE_RESIDENT_SUCCESS), which is what the
                      // Lease Data tab reads — so re-fetching it drops the removed transfer
                      // lease. (ENG-30823)
                      if (isValidResidentId(residentId)) {
                        this.props.actions.getOneResident(residentId);
                      }
                    }}
                    onCompleteSuccess={() => {
                      const { residentId } = this.props;
                      // Completing the transfer moves the household and activates the transfer
                      // lease server-side. getOneResident repopulates residentProfile.leases
                      // (GET_ONE_RESIDENT_SUCCESS) so the Lease Data tab renders the new active
                      // lease immediately. (ENG-30151 Sc8/Sc13)
                      //
                      // getHouseholdUnitPrograms re-derives which programs belong to the floor plan
                      // the household now occupies. It otherwise runs only in componentDidMount,
                      // and completing a transfer does not remount the profile — so without this
                      // the qualification tabs keep the PRE-transfer tagging until a page refresh,
                      // leaving "Start a Certification" enabled on a program the new floor plan no
                      // longer carries. (ENG-30974)
                      if (isValidResidentId(residentId)) {
                        this.props.actions.getOneResident(residentId);
                        this.props.actions.getHouseholdUnitPrograms(residentId);
                      }
                    }}
                  />
                ) : null
              }
              Household={
                <>
                  {isCommercial === true && (
                    <Tenant
                      householdId={householdId}
                      householdMembers={allHouseholdMembers}
                      editHouseholdMember={this.editOccupant}
                      residentId={this.props.residentId}
                      hasAssignedUnit={hasAssignedUnit}
                      isPriorResident={!this.props.isResident}
                      actualOrAnticipatedMoveOutDate={
                        actualOrAnticipatedMoveOutDate
                      }
                      setHasHouseholdRentableItems={
                        this.setHasHouseholdRentableItems
                      }
                      setHasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate={
                        this
                          .setHasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate
                      }
                    />
                  )}
                  {isCommercial === false && (
                    <Household
                      applicationId={applicationInfo.id}
                      disableEdit={!isResident}
                      editHouseholdMember={this.editOccupant}
                      onAddHouseholdMember={this.addOccupant}
                      onChangeHeadOfHouseholdSuccess={
                        this.handleMiniProfileRefresh
                      }
                      // Refresh Redux-backed household data after pet writes.
                      onSaveSuccess={this.handleMiniProfileSaveSuccess}
                      isAffordable={isAffordable}
                      householdId={householdId}
                      householdMembers={allHouseholdMembers}
                      onEdit={this.handleEditHousehold}
                      residentId={this.props.residentId}
                      vehicleCount={applicationInfo.vehicleCount}
                      unitId={unit ? unit.id : ''}
                      hasAssignedUnit={hasAssignedUnit}
                      isPriorResident={this.props.isResident ? false : true}
                      actualOrAnticipatedMoveOutDate={
                        actualOrAnticipatedMoveOutDate
                      }
                      setHasHouseholdRentableItems={
                        this.setHasHouseholdRentableItems
                      }
                      hasHouseholdRentableItems={
                        this.state.hasHouseholdRentableItems
                      }
                      isOnNTV={isOnNTV}
                      setHasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate={
                        this
                          .setHasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate
                      }
                    />
                  )}
                  {newestLease.desiredSignatureMethod === 'ELECTRONIC' &&
                    (newestLease.isRenewal || newestLease.isTransfer) && (
                      <LeaseSigning
                        leaseSignatureStatuses={leaseSignatureStatuses}
                        leaseTerms={this.props.leaseTerms}
                        softDeletedLeaseTerms={this.props.softDeletedLeaseTerms}
                        onGenerate={this.leaseDataHandleGenerateLease}
                        onSubmit={this.leaseDataHandleSubmit}
                        openFiscalPeriod={this.props.openFiscalPeriod}
                        prospectInfo={prospectInfo}
                        removeLease={this.removeLease}
                        residentId={residentId}
                        securityDeposits={securityDeposits}
                        selectedMonthlyOption={selectedMonthlyOption}
                        submittingRenewal={submittingRenewal}
                        transferInformation={transferInformation}
                        utilityAllowanceId={
                          householdUtilityAllowanceId ||
                          applicationInfo.householdUtilityAllowanceId
                        }
                        isReceivingAssistance={isReceivingAssistance}
                        startTransferMessage={startTransferMessage}
                        startRenewalMessage={startRenewalMessage}
                        underEviction={underEviction}
                        noticeToVacate={ntv}
                        nonOptionalCharge={nonOptionalCharge}
                      />
                    )}
                </>
              }
              Snapshot={
                <Snapshot
                  isResident={isResident}
                  initialValues={{ assignedToId: assignedTo?.id }}
                  assignedTo={assignedTo}
                  doNotRenew={doNotRenew}
                  underEviction={underEviction}
                  ledgerBalance={ledgerBalance}
                  monthsWithLateFees={monthsWithLateFees}
                  monthsWithNSFFees={monthsWithNSFFees}
                  users={users}
                  isUnitSoftDeleted={typeof unit?.deletedAt === 'string'}
                  handleAssignedToChange={this.handleAssignedToChange}
                  handleNoticeToVacateClick={this.handleNoticeToVacateClick}
                  handleDoNotRenewClick={this.handleDoNotRenewClick}
                  handleUnderEvictionClick={this.handleUnderEvictionClick}
                  handleRenewLease={this.handleRenewLease}
                  history={history}
                  householdInfo={householdInfo}
                  onProcessMoveOutClick={this.onProcessMoveOutClick}
                  fasReady={!!fasReady}
                  accountFinalized={!!applicationInfo.accountFinalized}
                  customerId={residentId}
                  manualWriteOff={this.props.actions.manualWriteOff}
                  currentLease={lease}
                  newestLease={parsedNewestLease}
                  showEditMoveInDateLink={showEditMoveInDateLink}
                  unitHistoryLink={unitHistoryLink}
                  hasOpenTransfer={hasOpenTransfer}
                  paymentRestrictions={paymentRestrictions}
                  isCommercial={isCommercial}
                  showEditPropertyMoveInDateLink={
                    showEditPropertyMoveInDateLink
                  }
                  showEditMoveOutDateLink={showEditMoveOutDateLink}
                  autoEmailMonthlyInvoices={autoEmailMonthlyInvoices}
                  handleAutoEmailMonthlyInvoiceClick={
                    this.handleAutoEmailMonthlyInvoiceClick
                  }
                  applicationId={applicationInfo.id}
                  propertyId={this.props?.selectedProperty?.id}
                  organizationId={this.props?.selectedProperty?.organizationId}
                  promptToaster={this.props.actions.promptToaster}
                  fasComplete={this.props.fasComplete}
                  propertyPaymentProvider={this.props.propertyPaymentProvider}
                  hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate={
                    hasHouseholdRentableItemEndDateAfterAnticipatedMoveOutDate
                  }
                  hasReOpenedRDCertifications={hasReOpenedRDCertifications}
                  residentInfo={residentInfo}
                  onCreateFinalAccountStatementReversal={
                    onCreateFinalAccountStatementReversal
                  }
                  householdHasMoveOutProratedTransactions={
                    hasMoveOutProratedTransactions
                  }
                  propertyAutoEmailMonthlyInvoice={
                    this.props?.applicationInfo?.property
                      ?.autoEmailMonthlyInvoice
                  }
                  householdId={householdId}
                  handleTransfer={this.handleTransfer}
                  transferReasonOptions={
                    this.props.transferMoveOutReasonOptions ?? []
                  }
                  isHUDByUnit={isHUDByUnit}
                  isSection236={isSection236}
                  floorPlanAffordablePrograms={
                    unit?.floorPlan?.floorPlanAffordablePrograms ?? []
                  }
                  selectedProperty={selectedProperty}
                />
              }
              Tabs={
                <Tabs
                  id="resident-profile-tabs"
                  value={selectedTabKey}
                  onChange={(key) => this.handleResidentProfileTabChange(key)}
                  affordableTabs={affordableTabs}
                  checkUserPermissions={this.checkUserPermissions}
                  flags={flags}
                  householdId={householdId}
                  hudTab={hudTab}
                  isCommercial={isCommercial}
                  // ENG-34106: the Housing Assistance tab keeps reading trunk's programs.
                  programs={resolveProfileTabPrograms({
                    isAffordable,
                    programs,
                  })}
                  prospectInfoId={this.props.prospectInfo.id}
                  selectedProperty={selectedProperty}
                  showAffordableTabs={showAffordableTabs}
                  showCamTab={showCamTab}
                  showDocumentsTab={showDocumentsTab}
                  showHUD={showHUD}
                  section236={isSection236}
                  showQualificationTab={isAffordable}
                  showTextingTab={showTextingTab}
                />
              }
              TabsContent={
                <>
                  {selectedTabKey === TAB_KEYS.ACTIVITY && (
                    <ActivityContainer
                      intl={this.props.intl}
                      prospectId={this.props?.prospectInfo.id || ''}
                      locale={this.props.locale}
                      onEditActivity={this.onEditActivity}
                      onCreateActivityClick={this.onCreateActivityClick}
                      updateTrigger={this.state.activityTableUpdateTrigger}
                    />
                  )}
                  {selectedTabKey === TAB_KEYS.WORK_ORDERS && (
                    <WorkOrderTab
                      householdId={householdId}
                      customerId={customerId}
                      unitId={unit && unit.id}
                      intl={this.props.intl}
                    />
                  )}

                  {selectedTabKey === TAB_KEYS.TEXTING && (
                    <TextingTab householdId={householdId} />
                  )}

                  {selectedTabKey === TAB_KEYS.LEDGER && (
                    <Ledger
                      customerStatus={status}
                      residentId={residentId}
                      customerId={customerId}
                      customerName={`${firstName} ${lastName}`}
                      frMembers={financiallyResponsibleHousehold}
                      frNames={financiallyResponsibleHousehold.map(
                        (f) => f.completeName,
                      )}
                      fasReady={!!fasReady}
                      accountFinalized={!!applicationInfo.accountFinalized}
                      fasDate={lease?.FASDate}
                      handleFasReadyClick={this.handleFasReady}
                      unit={unit}
                      household={householdInfo}
                      applicationId={applicationInfo.id}
                      isResident={true}
                      camPools={camPoolsForTransactions}
                      householdMembers={householdMembers}
                      lateMethodId={lease?.lateMethodId}
                      hasMoveOutProratedTransactions={
                        hasMoveOutProratedTransactions
                      }
                      moveOutProratedTransactionsDate={
                        moveOutProratedTransactionsDate
                      }
                    />
                  )}

                  {selectedTabKey === TAB_KEYS.LEASE_DATA && (
                    <>
                      {isCommercial === true && (
                        <CommercialLeaseDataTab
                          frNames={frNames}
                          leases={leases}
                          residentId={residentId}
                          isResident={isResident}
                          labelEndDate={labelEndDate}
                          selectedMonthlyOption={selectedMonthlyOption}
                          customer={pathOr(
                            {},
                            ['residentCustomer', 'customer'],
                            residentInfo,
                          )}
                          unit={unit || {}}
                          lateMethods={lateMethods || []}
                          onLeaseBasicsSubmit={
                            this.handleCommercialLeaseBasicsSubmit
                          }
                          applicationId={applicationInfo.id}
                          prospectInfo={prospectInfo}
                          isDisabledRenewal={isDisabledRenewal}
                          refreshLeases={this.handleRefreshLeases}
                          navigationHistory={history}
                          doNotRenew={doNotRenew}
                          underEviction={underEviction}
                          startRenewalMessage={startRenewalMessage}
                          noticeToVacate={ntv}
                        />
                      )}
                      {isCommercial === false && (
                        <LeaseDataTab
                          allAffordableTabsApproved={true}
                          unapprovedAffordablePrograms={[]}
                          applicationId={applicationInfo.id}
                          application={applicationInfo}
                          householdId={householdId}
                          hasOpenTransfer={hasOpenTransfer}
                          basicLeaseFees={basicLeaseFees}
                          doNotRenew={doNotRenew}
                          frNames={frNames}
                          handleRenewal={this.handleRenewal}
                          hasNonFR={nonFRAdults.length > 0}
                          history={history}
                          handleTransfer={this.handleTransfer}
                          handleTransferComplete={this.onCompleteTransferClick}
                          isDisabledRenewal={isDisabledRenewal}
                          isDisabledTransfer={isDisabledTransfer}
                          isResident={isResident}
                          labelEndDate={labelEndDate}
                          lateMethods={lateMethods}
                          leases={leases}
                          leaseRentPercentage={leaseRentPercentage}
                          leaseSignatureStatuses={leaseSignatureStatuses}
                          leaseTerms={this.props.leaseTerms}
                          softDeletedLeaseTerms={
                            this.props.softDeletedLeaseTerms
                          }
                          onGenerate={this.leaseDataHandleGenerateLease}
                          onSubmit={this.leaseDataHandleSubmit}
                          openFiscalPeriod={this.props.openFiscalPeriod}
                          prospectInfo={prospectInfo}
                          removeLease={this.removeLease}
                          residentId={residentId}
                          securityDeposits={securityDeposits}
                          selectedMonthlyOption={selectedMonthlyOption}
                          submittingRenewal={submittingRenewal}
                          transferInformation={transferInformation}
                          utilityAllowanceId={
                            householdUtilityAllowanceId ||
                            applicationInfo.householdUtilityAllowanceId
                          }
                          isReceivingAssistance={isReceivingAssistance}
                          startTransferMessage={startTransferMessage}
                          startRenewalMessage={startRenewalMessage}
                          underEviction={underEviction}
                          noticeToVacate={ntv}
                          nonOptionalCharge={nonOptionalCharge}
                          setProratedRentAmount={setProratedRentAmount}
                          setProratedOtherAmount={setProratedOtherAmount}
                          resetProratedRentAutoFilledChangeStatus={
                            this.resetProratedRentAutoFilledChangeStatus
                          }
                          handleAutoFilledFieldChange={
                            this.handleAutoFilledFieldChange
                          }
                          autoFilledLeaseChargeFields={
                            this.state.autoFilledLeaseChargeFields
                          }
                        />
                      )}
                    </>
                  )}

                  {selectedTabKey === TAB_KEYS.HOUSING_ASSISTANCE_VOUCHER && (
                    <HousingAssistanceVoucherTab
                      organizationId={selectedProperty.organizationId}
                      propertyId={selectedProperty.id}
                      householdId={householdId}
                      householdMoveInDate={moment(householdMoveInDate)}
                      transferDate={
                        isNil(transferDate) ? null : moment(transferDate)
                      }
                      stage="resident"
                      intl={this.props.intl}
                    />
                  )}

                  {selectedTabKey === TAB_KEYS.CAM && (
                    <CamTab
                      unit={unit}
                      selectedProperty={selectedProperty}
                      householdId={householdId}
                      intl={intl}
                      promptToaster={this.props.actions.promptToaster}
                      isPriorResident={this.props.isResident ? false : true}
                      onCamRefresh={onCamRefresh}
                    />
                  )}

                  {selectedTabKey === TAB_KEYS.LEASE_APPLICATION && (
                    <LeaseApplicationTab
                      intl={intl}
                      currentRecord={applicationInfo}
                      filteredResidents={residents}
                      applicantCancellationReasons={
                        applicantCancellationReasons
                      }
                      pendingApplicants={applicants.pendingApplicants}
                      canceledApplicants={applicants.canceledApplicants}
                      initialValues={{
                        applicationStatusId:
                          getApprovedStatusId(applicationStatuses),
                      }}
                      checkUserPermissions={this.checkUserPermissions}
                      applicationId={applicationInfo.id}
                      applicationDecisionStatus={applicationDecisionStatus}
                      applicationStatuses={applicationStatuses}
                      screening={screening}
                      generateAdverseActionLetter={
                        this.props.actions.generateAdverseActionLetter
                      }
                      screeningLetterTypes={this.props.screeningLetterTypes}
                      submitScreeningApplication={
                        this.props.actions.submitScreeningApplication
                      }
                      handleEditApplicationForm={this.handleEditApplicationForm}
                      handleChecklistOptionChange={
                        onChecklistOptionChangeCurried
                      }
                      currentUser={currentUser}
                      isResident={isResident}
                      approveApplicant={this.approveApplicant}
                      cancelApplicant={this.cancelApplicant}
                      selectedProperty={selectedProperty}
                      householdId={householdId}
                      handleMultipleChecklistOptionsChange={
                        this.handleMultipleChecklistOptionsChange
                      }
                      approveApplicantAction={
                        this.props.actions.approveApplicant
                      }
                      cancelApplicantAction={this.props.actions.cancelApplicant}
                      householdMembers={householdMembers}
                      mainCustomerId={mainCustomerId}
                    />
                  )}

                  {selectedTabKey === TAB_KEYS.SCREENING && (
                    <ScreeningTab
                      intl={this.props.intl}
                      applicationId={applicationInfo.id}
                      organizationId={selectedProperty?.organizationId}
                      propertyId={selectedProperty?.id}
                      residentId={this.props.residentId}
                      householdId={householdId}
                      householdMembers={[
                        ...this.props.householdMembers.filter(
                          ({ type, status }) =>
                            type === 'adult' && status !== 'Prior',
                        ),
                        ...this.props.houseHoldPendingMembers
                          .filter(
                            ({ status, type }) =>
                              status === 'Pending' && type !== 'pet',
                          )
                          .map((m) => ({
                            ...m,
                            isPendingApplicant: true,
                          })),
                      ]}
                    />
                  )}

                  {selectedTabKey === TAB_KEYS.DOCUMENTS && (
                    <ManageDocuments
                      intl={this.props.intl}
                      applicationId={applicationInfo.id}
                      customerType="resident"
                      customerName={`${firstName} ${lastName}`}
                      deletedApplicants={deletedApplicants}
                      isAffordable={isAffordable}
                      isCommercial={isCommercial}
                      leaseId={lease.id}
                    />
                  )}

                  {selectedTabKey === TAB_KEYS.PROSPECT_INFO && (
                    <ProspectInfoTab
                      intl={this.props.intl}
                      prospect={prospectInfo}
                      petTypes={this.props.petTypes}
                      contactTypes={this.props.contactTypes}
                      referralTypes={this.props.referralTypes}
                    />
                  )}

                  {showAffordableTabs &&
                    affordableTabs.map(
                      ({
                        affordableQualificationId,
                        programId,
                        programName,
                        urlProgramName,
                        propertyAffordableProgramId,
                        isPreviousProgram,
                        effectiveSpans,
                        gapSpans,
                        hasNoResolvableSpan,
                      }) => {
                        const commonProps = {
                          applicationId: applicationInfo.id,
                          householdId,
                          history,
                          initialCertTab: this.props.certTab,
                          initialCertId: this.props.certId,
                          intl,
                          key: urlProgramName,
                          leases,
                          locale,
                          location: this.props.location,
                          propertyId: applicationInfo?.property?.id,
                          programId,
                          programName,
                          programs,
                          propertyAffordableProgramId,
                          recertDueDates,
                          residentId,
                          tabKey: selectedTabKey,
                          unit,
                          refetchProfile: () => {
                            this.props.actions.getOneResident(
                              this.props.residentId,
                            );
                          },
                          isPriorResident,
                          isPreviousProgram,
                          // ENG-31723 (Task 5, spec §4/§5): threaded from Task 2's payload via the
                          // tab object -- see configureResidentApplicantAffordableTabsDisplay.
                          effectiveSpans,
                          gapSpans,
                          hasNoResolvableSpan,
                        };
                        return selectedTabKey === urlProgramName ? (
                          <>
                            {isGeneralAffordableNonHUD(programName) ? (
                              <GeneralAffordableNonHUD
                                {...commonProps}
                                hasPendingApplicants={
                                  pendingApplicants.length > 0
                                }
                                agedUpOnlyPendingMajorityDate={
                                  agedUpOnlyPendingMajorityDate
                                }
                                hasPendingApplicantsMinorToAdult={pendingApplicants.some(
                                  (item) => item?.wasMinor,
                                )}
                                currentMoveInDate={currentMoveInDate}
                              />
                            ) : null}
                            {programName === PH_PROGRAM_NAME ? (
                              <GeneralAffordablePH
                                {...commonProps}
                                affordableQualificationId={
                                  affordableQualificationId
                                }
                                appliesForPhaseIn={appliesForPhaseIn}
                              />
                            ) : null}
                            {programName === RD_PROGRAM_NAME ? (
                              <GeneralAffordableRD
                                {...commonProps}
                                affordableQualificationId={
                                  affordableQualificationId
                                }
                                appliesForPhaseIn={appliesForPhaseIn}
                              />
                            ) : null}
                          </>
                        ) : null;
                      },
                    )}

                  {selectedTabKey === TAB_KEYS.HUD && showHUD && (
                    <GeneralAffordableHUD
                      history={history}
                      initialCertTab={this.props.certTab}
                      initialCertId={this.props.certId}
                      location={this.props.location}
                      tabKey={selectedTabKey}
                      affordableQualificationId={
                        hudTab?.affordableQualificationId
                      }
                      applicationId={applicationInfo.id}
                      hasPendingApplicants={pendingApplicants.length > 0}
                      agedUpOnlyPendingMajorityDate={
                        agedUpOnlyPendingMajorityDate
                      }
                      householdId={householdId}
                      propertyId={applicationInfo?.property?.id}
                      programId={hudTab.programId}
                      programName={hudTab.programName}
                      propertyAffordableProgramId={
                        hudTab.propertyAffordableProgramId
                      }
                      residentId={residentId}
                      recertDueDates={recertDueDates}
                      appliesForPhaseIn={appliesForPhaseIn}
                      isSection236={isSection236}
                      isPriorResident={isPriorResident}
                      isPriorHousehold={this.props.isPriorHousehold}
                      isResident={isResident}
                      intl={intl}
                      locale={locale}
                      refetchProfile={() => {
                        this.props.actions.getOneResident(
                          this.props.residentId,
                        );
                      }}
                      currentMoveInDate={currentMoveInDate}
                      currentMoveOutDate={currentMoveOutDate}
                      unit={unit}
                      leases={leases}
                      moveOutReasons={this.props.moveOutReasons}
                      noticeToVacate={residentInfo?.lease?.noticeToVacate}
                      isPreviousProgram={hudTab?.isPreviousProgram}
                      effectiveSpans={hudTab?.effectiveSpans}
                      gapSpans={hudTab?.gapSpans}
                      hasNoResolvableSpan={hudTab?.hasNoResolvableSpan}
                      residentAffordableQualifications={
                        affordableQualifications
                      }
                    />
                  )}
                </>
              }
            />

            <EditMoveInDateModal
              moveType="moveIn"
              show={this.state.modal === 'editMoveInDateModal'}
              onSubmit={this.handleMoveInDateModalSubmit}
              dismiss={this.resetModal}
              initialValues={editMoveInDateModalInitial}
              additionalValidation={editMoveInValidateFiscalPeriod}
              isHouseholdMiMoDateChangesEnabled={isMiMoDateChangesEnabled}
              residentId={this.props.residentInfo?.id}
              organizationId={this.props.selectedProperty?.organizationId}
              propertyId={this.props.selectedProperty?.id}
              currentMoveInDate={
                moveInStay?.moveInDate ??
                formatCalendarDateUtc(actualMoveInDate, 'YYYY-MM-DD')
              }
              stayLeaseId={moveInStay?.stayLeaseId}
              unitLabel={moveInStay?.unitLabel}
            />
            {this.state.modal === 'unitHistoryModal' && (
              <UnitHistoryModal
                residentId={this.props.residentInfo?.id ?? ''}
                organizationId={
                  this.props.selectedProperty?.organizationId ?? ''
                }
                propertyId={this.props.selectedProperty?.id ?? ''}
                onClose={this.resetModal}
                onEditStay={this.showStayMoveInDateModal}
              />
            )}
            <EditMoveOutDateModal
              moveType="moveOut"
              show={this.state.modal === 'editMoveOutDateModal'}
              onSubmit={this.handleMoveOutDateModalSubmit}
              dismiss={this.resetModal}
              initialValues={editMoveOutDateModalInitial}
              // Flag on: the server rules come from the correction preview instead.
              additionalValidation={
                isMiMoDateChangesEnabled ? undefined : editMoveOutValidation
              }
              isRDFloor={isRDByUnit}
              intl={intl}
              isHouseholdMiMoDateChangesEnabled={isMiMoDateChangesEnabled}
              residentId={this.props.residentInfo?.id}
              organizationId={this.props.selectedProperty?.organizationId}
              propertyId={this.props.selectedProperty?.id}
              currentMoveOutDate={
                isResident
                  ? null
                  : formatCalendarDateUtc(
                      currentLease?.moveOutDate,
                      'YYYY-MM-DD',
                    )
              }
            />
            <EditPropertyMoveInDateModal
              show={this.state.modal === 'editPropertyMoveInDateModal'}
              onSubmit={this.handlePropertyMoveInDateModalSubmit}
              dismiss={this.resetModal}
              initialValues={editPropertyMoveInDateModalInitial}
              openFiscalPeriod={openFiscalPeriod}
              isHouseholdMiMoDateChangesEnabled={
                isHouseholdMiMoDateChangesEnabled
              }
            />
            <EditOccupantModal
              affordableRelationships={affordableRelationships}
              countries={countries}
              initialValues={{
                ...occupantToEdit.residentInfo,
                reportingInformation: reportingInformationInitialValues,
              }}
              isAffordable={isAffordable}
              isCommercial={isCommercial}
              selectedProperty={selectedProperty}
              unit={unit}
              occupantInfo={occupantToEdit}
              onClose={this.resetModal}
              onSubmit={this.handleEditOccupantSubmit}
              sendPayLeaseEmail={this.sendPayLeaseEmail}
              show={this.state.modal === 'editOccupantForm'}
              states={states}
              suffixes={suffixes}
              unitNumber={pathOr('', ['units', '0', 'number'], currentLease)}
              newestLease={newestLease}
              relationshipOptions={relationshipOptions}
              applicantTypeOptions={applicantTypeOptions}
              applicant={miniProfileApplicant}
              flags={flags}
            />
            <AddHouseholdMemberModal
              open={this.state.modal === 'addHouseholdMemberType'}
              onClose={this.resetModal}
              onContinue={this.handleAddHouseholdMemberTypeContinue}
            />
            <MiniProfileBridge
              open={this.state.modal === 'editAffordableMiniProfile'}
              title={
                this.state.occupantToEdit
                  ? occupantToEdit?.name ?? ''
                  : // Named after the type picked in the chooser. Read off
                  // newOccupantInfo (not state.newMemberType) so the title and
                  // the form the Mini-Profile actually builds can't disagree.
                  newOccupantInfo.type === 'minor'
                  ? 'Add Minor'
                  : 'Add Adult'
              }
              onClose={this.handleMiniProfileClose}
              occupantInfo={
                this.state.occupantToEdit ? occupantToEdit : newOccupantInfo
              }
              residentId={this.props.residentId}
              suffixes={suffixes}
              relationshipOptions={relationshipOptions}
              minorRelationshipOptions={minorRelationshipOptions}
              applicantTypeOptions={applicantTypeOptions}
              agedUpMinorApplicantTypeId={agedUpMinorApplicantTypeId}
              guarantorOptions={guarantorOptions}
              liveInCaretakerOptions={liveInCaretakerOptions}
              states={states}
              countries={countries}
              affordableRelationships={affordableRelationships}
              specialNeedsDesignations={specialNeedsDesignations}
              onSubmit={this.handleEditOccupantSubmit}
              isAffordable={isAffordable}
              isCommercial={isCommercial}
              isPriorResident={this.props.isPriorHousehold}
              onSaveSuccess={this.handleMiniProfileRefresh}
            />
          </NoPrint>
        </DocumentTitle>
      </LeaseDataTabProvider>
    );
  }
}

ResidentProfile.contextTypes = {
  store: PropTypes.any,
};

export const mapStateToProps = (
  state: GlobalState,
  ownProps: Object,
): Props => {
  const { app, residentProfile, leaseDataTab, languageProvider } = state;

  const residentIdFromParams = ownProps?.match?.params?.residentId;
  const residentId = residentIdFromParams
    ? residentIdFromParams.split('?').shift()
    : '';
  const query = parse(ownProps.location.search.replace('?', ''));
  const activeTab = getActiveResidentProfileTab(query?.tab);
  return {
    residentId,
    activeTab,
    certTab: query?.certTab ?? null,
    certId: query?.certId ?? null,
    activityTypes: app.activityTypes,
    affordableRelationships: getAffordableRelationshipOptions(state),
    relationshipOptions: getRelationshipOptions(state),
    minorRelationshipOptions: getMinorRelationshipOptions(state),
    applicantTypeOptions: getApplicantTypeOptions(state),
    agedUpMinorApplicantTypeId: getAgedUpMinorApplicantTypeId(state),
    applicantCancellationReasons: app.applicantCancellationReasons,
    applicants: residentProfile.applicants,
    applicationDecisionStatus: residentProfile.applicationDecisionStatuses,
    applicationInfo: {
      ...residentProfile.applicationInfo,
      au: Array.isArray(residentProfile.unit?.au)
        ? residentProfile.unit.au[0]
        : undefined,
    },
    applicationQualificationInfo: residentProfile.applicationQualificationInfo,
    applicationStatuses: getApplicationStatusOptions(state),
    assignedTo: residentProfile.assignedTo,
    basicLeaseFees: residentProfile.basicLeaseFees,
    columnOrder: residentProfile.columnOrder,
    completedActivities: residentProfile.completedActivities,
    completedActivitiesMeta: residentProfile.completedActivitiesMeta,
    allActivities: residentProfile.allActivities,
    allActivitiesMeta: residentProfile.allActivitiesMeta,
    contactTypes: app.contactTypes,
    countries: getCountryOptions(state),
    currentLease: getCurrentLease(state, ownProps.flags),
    currentUser: getCurrentUser(state),
    deletedApplicants: residentProfile.deletedApplicants,
    financiallyResponsibleHousehold: getFinanciallyResponsibleResidents(state),
    frPendingApplicants: getFinanciallyResponsiblePendingApplicants(state),
    householdMembers: getHouseholdResidentMembers(state),
    houseHoldPendingMembers: getHouseholdPendingMembers(state),
    isFutureLease: getIsFutureLease(state),
    isRenewalComplete: isRenewalComplete(state),
    hasOpenTransfer: hasOpenTransferLease(state),
    hasOpenRenewal: hasOpenRenewalLease(state),
    housingSituations: getHousingSituationOptions(state),
    isResident: isCurrentResident(state),
    isPriorHousehold: isPriorHousehold(state),
    isPriorResident: isPriorResident(state),
    labelEndDate: getLabelEndDate(state),
    lateMethods: getLateMethods(state),
    leases: residentProfile.leases,
    leaseSignatureStatuses: leaseDataTab.leaseSignatureStatuses,
    leaseTerms: residentProfile.leaseTerms,
    softDeletedLeaseTerms: residentProfile.softDeletedLeaseTerms,
    ledgerBalance: residentProfile.ledgerBalance,
    locale: languageProvider.locale,
    monthlyTransactions: getMonthlyTransactions(state),
    monthlyTransactionsForProrateCalculator:
      getMonthlyTransactionsForProrateCalculator(state),
    monthsWithLateFees: residentProfile.monthsWithLateFees,
    monthsWithNSFFees: residentProfile.monthsWithNSFFees,
    moveOutReasons: getMoveOutReasonsOptions(
      state,
      DEPRECATED_MOVE_OUT_REASON_IDS,
    ),
    transferMoveOutReasonOptions: (app.moveOutReasons ?? [])
      .filter((r) => r.type === 'Transfer')
      .map((r) => ({ value: r.id, text: r.reason })),
    openFiscalPeriod: residentProfile.openFiscalPeriod,
    isFirstFiscalPeriod: residentProfile.isFirstFiscalPeriod,
    pendingActivities: residentProfile.pendingActivities,
    petBreeds: app.petBreeds,
    petTypes: app.petTypes,
    previousLeaseId: getPreviousLeaseId(state),
    propertyClassType: getSelectedPropertyClassType(state),
    prospectInfo: residentProfile.prospectInfo,
    reCompleteCertificationStarted: getRecompleteCertificationStatus(state),
    referralTypes: app.referralTypes,
    residentInfo: residentProfile.residentInfo,
    residents: getApprovedResidents(state),
    screening: residentProfile.screening,
    securityDeposits: getSecurityDeposits(state),
    selectedMonthlyOption: residentProfile.selectedMonthlyOption,
    selectedProperty: app.selectedProperty,
    specialNeedsDesignations: getSpecialNeedsDesignationOptions(state),
    states: getStateOptions(state),
    submittingRenewal: residentProfile.submittingRenewal,
    subsidyBalance: residentProfile.subsidyBalance,
    suffixes: getNameSuffixOptions(state),
    transferInformation: residentProfile.transferInformation,
    unit: residentProfile?.unit,
    users: getProspectAssigneesOptions(state),
    affordableQualifications: residentProfile.affordableQualifications,
    affordableException: residentProfile.affordableException,
    affordableSetup: residentProfile.affordableSetup,
    householdAffordableDetails: residentProfile.householdAffordableDetails,
    recertDueDates: residentProfile.recertDueDates,
    firstUnitMoveInAfterPriorResident:
      residentProfile.firstUnitMoveInAfterPriorResident,
    screeningLetterTypes: getScreeningLetterTypes(state),
    camPools: residentProfile.camPools,
    moveInApplyFullAmountToProRate: pathOr(
      false,
      ['app', 'selectedProperty', 'moveInApplyFullAmountToProRate'],
      state,
    ),
    prorateMethods: app?.prorateMethods ?? [],
    moveOutProrateMethods: app?.moveOutProrateMethods ?? [],
    moveOutProratedRentMethodId:
      state?.app?.selectedProperty?.moveOutProrateMethodId,
    deprecated_moveOutProratedRentMethodId:
      state?.app?.selectedProperty?.deprecated_moveOutProrateMethodId,
    moveOutProratedTransactions: app?.moveOutProratedTransactions ?? [],
    fpNonOptionalCharges: residentProfile?.fpNonOptionalCharges ?? [],
    fasComplete:
      parse(ownProps.location.search.replace('?', ''))?.fasComplete ?? false,
    socketFailed: residentProfile?.socketFailed,
    propertyPaymentProvider: app?.propertyPaymentMethod?.paymentProvider,
    propertyHasHUD: (app?.selectedProperty?.pap ?? []).some(
      (pap) => pap?.masterAffordableProgram?.name === 'HUD',
    ),
    guarantorOptions: getGuarantorOptions(state),
    liveInCaretakerOptions: getLiveInCaretakerOptions(state),
    isMoveOutRequestProcessing:
      residentProfile?.isMoveOutRequestProcessing ?? false,
    floorPlanAtMoveOut: getFloorPlanAtMoveOut(state),
    propertyAffordablePrograms:
      residentProfile?.propertyAffordablePrograms ?? [],
  };
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      ...residentActions,
      ...prospectActions,
      ...applicationActions,
      change,
      assignApplication,
      getAllActivityTypes,
      getAllAffordableRelationships,
      getAllApplicantTypes,
      getAllRelationships,
      getAllApplicantCancellationReasons,
      getAllAssignees,
      getAllContactTypes,
      getAllCountries,
      getAllMoveOutReasons,
      getAllHousingSituations,
      getAllNameSuffixes,
      getAllPetBreeds,
      getAllPetTypes,
      getAllProspectStatus,
      getAllReferralTypes,
      getAllStates,
      getCustomerLedgerHeader,
      refreshLedger,
      getResidentHousehold,
      getSignatureStatuses,
      getAllDocuments,
      setDocumentsListStatus,
      getAllProrateMethods,
      getAllMoveOutProrateMethods,
      getStartOfMonthMoveOutProratedTransactons,
      getAllSpecialNeedsDesignations,
      selectProperty,
      updateSelectedProperty,
      submit,
      updateApplication,
      getLiveInCaretakerOptions,
      promptToaster,
    },
    dispatch,
  );
  return { actions };
}

const InjectedResidentProfile = injectIntl(ResidentProfile);
// ENG-29435: withTransferTargetPrograms sits INSIDE connect so it can read selectedProperty and
// residentInfo, and inside withLDConsumer so it can read flags.
export default withLDConsumer()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withTransferTargetPrograms(InjectedResidentProfile)),
);
